import { IndependentPreviewGroup, PreviewGroupStateProvider } from "./IndependentPreviewGroup";
import { independentPreviewGroups, textWithoutPreviewGroupSuggestions, orderedPreviewPresentation } from "./independentPreviewGroups";
import { parseRewriteOptions, rewriteOptionsCanApply } from '~shared/rewriteOptions';
import { ChatErrorBoundary } from "./PersistentChatErrorBoundary";
import { LiteralReplacementResult } from "./LiteralReplacementResult";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode } from "react";
import InlineSVG from "react-inlinesvg";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AppEventPublisher, AgenticChatFeedback } from "src/AppEventPublisher";
import { cn } from "src/lib/cn";
import { ChatMessageBubble, AssistantMessageBody, StreamingSparkle, UserMessageBubble as SharedUserMessageBubble, ChatMessageActionRow, defaultChatIcons } from "@sudowrite/shared-chat";
import { entityGenerateUserMessageDisplayContent } from "~shared/agenticChat/entityGenerateUserMessageContent";
import { parseStoryBibleGeneratedEntity } from "~shared/agenticChat/storyBibleGeneratedEntity";
import {
  buildAgenticWriteButtonSettingsFromEditor,
  usePersistentChatStore,
} from "src/stores/persistentChatStore";
import { StoryBibleGeneratedEntityCard } from "./StoryBibleGeneratedEntityCard";
import {
  hasSuccessfulSelectionRewriteManuscriptApply,
  manageProjectDocumentsSummaryIndicatesManuscriptApplied,
} from "~shared/agenticManuscriptPlacementSignals";
import { Text } from "src/sudokit/components/Text";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { useAppStore } from "src/stores/appStore";
import { useProjectStore } from "src/stores/projectStore";
import { sendInvocationEvent } from "src/Utils";
import { PersistentChatMessageData } from "~shared/types/persistent-chat-types";
import { AgenticOutlineUpdate, AgenticToolRun, usePersistentChat } from "./PersistentChatContext";
import type { AgenticCharacterUpdate, AgenticWorldbuildingUpdate, AgenticMetaUpdate } from "src/stores/persistentChatStore";
import InlineWarning from "../InlineWarning";
import { copyHtmlToClipboard, derivePlainTextFromHtml } from "./copyUtils";
import { extractPlainTextFromMentions, stripTrailingDocumentMentionsFromDisplay } from "./mentions/mentionFormatUtils";
import { ThinkingStep } from "./ThinkingStep";
import { isCommentingKeepingChatTurnOpen } from "./commentingTurnState";
import type { ThinkingStepData, CTAAction } from "./ThinkingStep";
import {
  parseAgenticWriteButtonSettingsFromTurnContext,
  parseWritePovTenseChiclets,
  resolveWriteModelForThinkingChiclet,
  resolveUserGuidanceForWriteChiclets,
  resolveWriteSettingsForThinkingChiclets,
  type WritePovTenseChiclet,
} from "./ThinkingStep/writeSettingsForChiclets";
import { Textarea } from "src/sudokit/components/Textarea";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import GenericPopover from "src/sudokit/components/Popover/GenericPopover";
import Auth from "src/Auth";
import { PlanCard } from "./Plan/PlanCard";
import type { PlanData } from "src/stores/persistentChatStore";
import { CommentingCard } from "./CommentingCard";
import type { CommentingData } from "src/stores/persistentChatStore";
import {
  messageHasFinalManuscriptChange,
  extractProposeManuscriptChangeOperation,
  manuscriptChangeAppliedSuggestionIndex,
  manuscriptChangeCanUseRewriteCarousel,
  manuscriptChangeUsesCompactResult,
  manuscriptChangeIsReadOnlyHistory,
  manuscriptChangeRefShowsDurableUndo,
  manuscriptChangeSuggestionCardPending,
  manuscriptChangeSuggestionEntries,
  proposeManuscriptChangeToolAwaitingSuggestionCard,
  manuscriptChangeSuggestionTexts,
  manuscriptChangeStatusShowsDurableUndo,
  normalizeManuscriptChangeSuggestions,
  stripRedundantManuscriptChangeBeforeAfterProse,
  parseManuscriptChangeMessageRef,
  resolveManuscriptChangeDocumentId,
} from "./manuscriptChangeMessageRef";
import {
  approveManuscriptChange,
  applyManuscriptChangeSet,
  safeManuscriptChangeApplyFailureMessage,
  undoManuscriptChangeSet,
} from "src/lib/manuscriptChangeActions";
import * as Sentry from "@sentry/react";
import { dispatchAppliedManuscriptChangeHighlight } from "src/lib/dispatchAppliedManuscriptChangeHighlight";
import {
  isManuscriptStagingToolName,
} from "~shared/manuscriptStagingThinking";
import { normalizeTerminalPlanStatus } from "src/lib/normalizeTerminalPlanStatus";
import { getProseActionIcon } from "./proseActions";
import { UserQuestionAnswerBubble } from "./AskUserQuestion";
import { ChatBugReportForm } from "./ChatBugReportForm";
import { AgenticChatFeedbackPopover } from "./AgenticChatFeedbackPopover";
import {
  findPrecedingUserMessageId,
  patchChatFeedbackRatingInCache,
  shouldShowRepeatedDislikeRecovery,
} from "./agenticFeedbackRecovery";
import { ChatSupportRequestForm } from "./ChatSupportRequestForm";
import { createChatMarkdownComponents } from "./chatMarkdownComponents";
import { agenticChatTransformLinkUri } from "./chatMarkdownLinkUri";
import { hideIncompleteMarkdownLinksAtEnd, normalizeParentheticalCommentLinks, stripThinkTagsFromAssistantOutput, tightenMarkdownListSpacing } from "./streamingMarkdown";
import { MentionMarkdown } from "./mentions/MentionMarkdown";
import { remarkDocumentMentions } from "./remarkDocumentMentions";
import RewriteSuggestionCarousel from "../RewriteSuggestionCarousel";
import {
  parseRewriteSuggestionsFromText,
  extractRewriteSuggestionsFromPersistedToolExecutions,
  messageHasRewriteSuggestionCarouselMetadata,
} from "../RewriteSuggestionCarousel/rewriteSuggestionParser";
import { toolResultAllowsCarouselRender } from "../../../../src/shared/writeCarouselRenderGate";
import { useCommentStore } from "src/stores/commentStore";
import {
  applySuggestionToEditor,
  applySuggestionToEmptyDocument,
  applySuggestionToPassageInEditor,
  applySuggestionToSelectionInEditor,
  applySuggestionAtCursorInEditor,
  applySuggestionReplacingSiblingCarouselOption,
  passageMatchWouldReplaceEntireDocument,
  buildEditorSelectionHintForPassage,
  passageMatchesDocRangeText,
  isEditorDocumentEffectivelyEmpty,
} from "src/utils/tiptapSuggestion";
import { useToast } from "../ToastWrapper";
import { questionIsFeedbackTypesPicker } from "~shared/manuscriptFeedbackSilentMarker";
import { hasRestorableAgenticSnapshotBundle } from "src/lib/agenticChatSnapshotBundle";
import { KeybardShortcutIcon } from "src/components/KeyboardShortcutIcon";
import {
  agenticChatRevertOutcomeToast,
  applyStoryBibleRefreshAfterRevert,
  parseAgenticChatRevertResponse,
  storyBibleRefreshHintFromRestoredEntities,
} from "src/lib/storyBibleRefreshAfterRevert";
import { isStoryBibleCtaType } from "./storyBibleCta";
import { renderMarkdownForTiptapInsert } from "src/lib/renderMarkdownForTiptap";
import { buildSuggestionDocumentContent } from "src/lib/suggestionDocumentContent";

/** Outcome line shown under the carousel when prose was already streamed or lives in the cards. */
const MANUSCRIPT_PLACEMENT_OUTCOME_TAIL_RE =
  /(?:^|\n\n)(I've (?:inserted the rewrite into your manuscript|rewritten your selection and updated the manuscript)\.[\s\S]*)$/i;

function chatTextBelowRewriteCarousel(assistantMarkdown: string): string | null {
  const trimmed = assistantMarkdown.trim();
  if (!trimmed) return null;
  const tailMatch = trimmed.match(MANUSCRIPT_PLACEMENT_OUTCOME_TAIL_RE);
  if (tailMatch?.[1]) return tailMatch[1].trim();
  if (/^I've (?:inserted the rewrite|rewritten your selection)/i.test(trimmed)) {
    return trimmed;
  }
  return null;
}

type RevertOption = {
  snapshotId?: string;
  outlineId?: string;
  projectId?: string;
  chatId: string;
  userMessageId: string;
  userMessageContent: string;
  snapshotBundle?: {
    outlineSnapshotId: string | null;
    outlineId: string | null;
    characterSnapshots: Record<string, string>;
    worldbuildingSnapshots: Record<string, string>;
    metaSnapshot: unknown;
    createdCharacterIds?: string[];
    createdWorldbuildingIds?: string[];
  };
};

type Props = {
  message: PersistentChatMessageData;
  showInlineTerminalPlan?: boolean;
  showInlineTerminalCommenting?: boolean;
  searchedDocs?: string[];
  lookingAtData?: string[];
  cutoffData?: { originalLength: number; newLength: number } | null;
  contextWarning?: {
    message: string;
    percentageIncluded: number;
    totalMessages: number;
    includedMessages: number;
  } | null;
  streaming?: boolean;
  toolRuns?: AgenticToolRun[];
  outlineUpdates?: AgenticOutlineUpdate[];
  characterUpdates?: AgenticCharacterUpdate[];
  worldbuildingUpdates?: AgenticWorldbuildingUpdate[];
  metaUpdates?: AgenticMetaUpdate[];
  thinkingSteps?: ThinkingStepData[];
  revertOption?: RevertOption;
  /** Assistant message that owns the restorable snapshot (may differ on chat-only tail fallback). */
  revertTargetAssistantMessageId?: string;
  /** Whether the inline edit affordance (pencil) may appear under this user bubble. */
  canShowInlineEditForUserMessageId?: (messageId: string) => boolean;
  onEditTailUserMessage?: (userMessageId: string) => void;
  inlineEditUserMessageId?: string | null;
  onCancelInlineEditUserMessage?: () => void;
  onSubmitInlineEditUserMessage?: (userMessageId: string, text: string) => void;
  isLastMessage?: boolean;
  isLastAssistantMessage?: boolean;
  isLastRevertibleAssistantMessage?: boolean;
  isRevertedStoryBibleChange?: boolean;
  onRevertStoryBibleSuccess?: (assistantMessageId: string) => void;
  streamingModel?: string | null;
  streamingWritePovTenseChiclets?: WritePovTenseChiclet[] | null;
  streamingContextElements?: string[];
  streamingMentionedEntities?: Array<{ type: string; name: string }>;
  mode?: "write" | "chat";
  initialRating?: number | null;
  questionAnswer?: { text: string; hasAppendedDocumentMentions: boolean } | null;
  ctaAction?: CTAAction;
  onCtaGo?: (customInstructions: string, extra?: { role?: string; fields?: Array<{ id?: string; label: string; value?: unknown }> }) => void;
  onCtaCancel?: () => void;
  showResendLastMessage?: boolean;
  onResendLastMessage?: () => void;
  /** Duplicates a terminal plan server-side (Write mode, no agent stream). */
  onDuplicatePlanRetry?: (plan: PlanData) => void;
  /** Active manuscript document (for sudowrite://comment/ links in assistant markdown). */
  activeDocumentId?: string | null;
  /** Current Firebase project (sibling-book comment links open in a new tab). */
  activeProjectId?: string | null;
  /** Called when the user clicks the retry button after a failed comment creation turn. */
  onRetryCommentCreation?: () => void;
  feedbackByMessageId?: Record<string, number>;
  onFeedbackTryAgain?: (params: {
    assistantMessageId: string;
    precedingUserMessageId: string | null;
    mode: "write" | "chat";
    feedbackText?: string;
  }) => void;
  onFeedbackSwitchToChatMode?: () => void;
  onFeedbackStartFreshChat?: () => void;
  /** Branch pager: current variant index among user siblings at this fork (0-based). */
  userBranchSiblingIndex?: number;
  userBranchSiblingCount?: number;
  onUserBranchPrev?: () => void;
  onUserBranchNext?: () => void;
};

function normalizeMessageContent(content: unknown): string {
  if (typeof content === "string") return content;
  if (content == null) return "";
  if (typeof content === "number" || typeof content === "boolean" || typeof content === "bigint") {
    return String(content);
  }
  try {
    const serialized = JSON.stringify(content);
    if (typeof serialized === "string") return serialized;
  } catch {
    // Fall through to generic conversion.
  }
  return String(content);
}

const iconTypeMap = {
  characters: "/assets/story-bible/characters-icon.svg",
  worldbuilding: "/assets/story-bible/worldbuilding-icon.svg",
  outline: "/assets/story-bible/outline-icon.svg",
  synopsis: "/assets/story-bible/synopsis-icon.svg",
  genre: "/assets/story-bible/genre-icon.svg",
};

function inferRelativeRewriteTarget(introText: string): string | null {
  const normalized = String(introText || "").toLowerCase().replace(/\s+/g, " ").trim();
  if (!normalized) return null;
  if (/\b(last|final)\s+sentence\b/.test(normalized)) return "last sentence";
  if (/\b(first|opening|initial)\s+sentence\b/.test(normalized)) return "first sentence";
  return null;
}

type RewriteEditorSelectionHint = {
  documentId?: string;
  pmFrom?: number;
  pmTo?: number;
  plainTextStart?: number;
  plainTextEnd?: number;
};

function parseRewriteTurnContext(raw: unknown): {
  passage?: string;
  documentId?: string;
  editorSelectionHint?: RewriteEditorSelectionHint;
} | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const editorSelectionHint = rec.editorSelectionHint as RewriteEditorSelectionHint | undefined;
  const documentIdFromHint =
    typeof editorSelectionHint?.documentId === "string" && editorSelectionHint.documentId.trim()
      ? editorSelectionHint.documentId.trim()
      : undefined;
  const documentIdFromTurn =
    typeof rec.documentId === "string" && rec.documentId.trim() ? rec.documentId.trim() : undefined;
  return {
    passage: typeof rec.passage === "string" ? rec.passage : undefined,
    documentId: documentIdFromHint ?? documentIdFromTurn,
    editorSelectionHint,
  };
}

function resolveRewriteApplyDocumentId(params: {
  turnContext: ReturnType<typeof parseRewriteTurnContext>;
  messageMetadata: Record<string, unknown>;
  activeDocumentId?: string;
}): string | undefined {
  const fromTurn = params.turnContext?.documentId?.trim();
  if (fromTurn) return fromTurn;
  const fromAssistantMeta =
    typeof params.messageMetadata.documentId === "string" ? params.messageMetadata.documentId.trim() : "";
  if (fromAssistantMeta) return fromAssistantMeta;
  const fromComment =
    typeof params.messageMetadata.referencedCommentDocumentId === "string"
      ? params.messageMetadata.referencedCommentDocumentId.trim()
      : "";
  if (fromComment) return fromComment;
  return params.activeDocumentId;
}

function storedEditorSelectionHintStillValid(
  tiptap: { state?: { doc?: { textBetween: (from: number, to: number, blockSep?: string, leafSep?: string) => string } } },
  hint: RewriteEditorSelectionHint | undefined,
  anchorTexts: string[],
  documentId: string | undefined,
): boolean {
  if (!hint || !documentId || String(hint.documentId ?? "") !== String(documentId)) return false;
  const pmFrom = Number(hint.pmFrom);
  const pmTo = Number(hint.pmTo);
  if (!Number.isFinite(pmFrom) || !Number.isFinite(pmTo) || pmTo <= pmFrom) return false;
  try {
    const pmText = tiptap.state?.doc?.textBetween(pmFrom, pmTo, "\n", "\0") ?? "";
    if (!pmText.trim()) return false;
    const anchors = anchorTexts.map((anchor) => String(anchor ?? "").trim()).filter(Boolean);
    if (anchors.length === 0) return true;
    return anchors.some((anchor) => passageMatchesDocRangeText(pmText, anchor));
  } catch {
    return false;
  }
}

type ManageProjectDocumentsArgs = {
  operation?: unknown;
  edit?: {
    mode?: unknown;
    new_text?: unknown;
    content?: unknown;
    text?: unknown;
  } | unknown;
  initial_content?: unknown;
  review_instruction?: unknown;
  marker_edits?: unknown;
  edits?: unknown;
};

const RevertAgentChangesButton: React.FC<{
  revertOption: RevertOption;
  assistantMessageId: string;
  snapshotBundle: RevertOption["snapshotBundle"];
  label: string;
  streaming?: boolean;
  isLastRevertibleAssistantMessage?: boolean;
  buttonClassName?: string;
  onRevertSuccess?: () => void;
  /** Shared across sibling revert buttons on the same assistant message. */
  revertInFlight: boolean;
  onRevertStart: () => void;
  onRevertEnd: () => void;
}> = ({
  revertOption,
  assistantMessageId,
  snapshotBundle,
  label,
  streaming,
  isLastRevertibleAssistantMessage,
  buttonClassName,
  onRevertSuccess,
  revertInFlight,
  onRevertStart,
  onRevertEnd,
}) => {
  const { chatId, refetch } = usePersistentChat();
  const setToast = useToast()?.setToast;
  const [state, setState] = useState<"idle" | "reverting" | "done">("idle");

  if (streaming || !isLastRevertibleAssistantMessage || !hasRestorableAgenticSnapshotBundle(snapshotBundle)) {
    return null;
  }

  const handleRevert = async () => {
    if (!snapshotBundle || !revertOption.projectId || revertInFlight || state !== "idle") return;

    onRevertStart();
    setState("reverting");
    AppEventPublisher.agenticChat.revert(1, Boolean(snapshotBundle.outlineSnapshotId));

    try {
      const response = await Auth.fetchWithIdToken("/api/ai/agenticChat/revert", {
        method: "POST",
        chatId: revertOption.chatId || chatId,
        projectId: revertOption.projectId,
        messageId: revertOption.userMessageId,
        assistantMessageId,
        snapshotBundle,
      });

      const { restoredEntities, errors, restoredFieldPaths } = parseAgenticChatRevertResponse(response);
      const restoredAnyEntity = restoredEntities.length > 0;

      if (restoredAnyEntity) {
        await applyStoryBibleRefreshAfterRevert(
          revertOption.projectId,
          storyBibleRefreshHintFromRestoredEntities(restoredEntities, restoredFieldPaths),
        );
      }

      await refetch();
      if (!restoredAnyEntity) {
        setToast?.("Could not revert this turn because no Story Bible changes were restored.");
        setState("idle");
      } else {
        setToast?.(agenticChatRevertOutcomeToast(restoredEntities, errors));
      }
      if (restoredAnyEntity && errors.length === 0) {
        setState("done");
        onRevertSuccess?.();
      } else if (errors.length > 0) {
        setState("idle");
      }
    } catch {
      setToast?.("Could not revert changes. Try History on the Story Bible card.");
      setState("idle");
    } finally {
      onRevertEnd();
    }
  };

  if (state === "done") {
    return null;
  }

  const blockedBySibling = revertInFlight && state === "idle";
  const canClick = state === "idle" && !blockedBySibling;
  const iconClass = cn(
    "w-5 h-5 [&_path]:fill-app-grey-1",
    state === "reverting" && "animate-pulse opacity-60",
  );

  return (
    <SudoTooltip text={state === "reverting" ? "Reverting…" : label} position="top" delay={500}>
      <WithReset
        onClick={canClick ? handleRevert : undefined}
        className={cn(
          "pt-px cursor-pointer hover:scale-105 transition-all duration-200 ease-in-out active:scale-95",
          buttonClassName,
          !canClick && "opacity-50 cursor-not-allowed pointer-events-none",
        )}
        aria-label={label}
        aria-disabled={!canClick}
      >
        <InlineSVG className={iconClass} src="/assets/editor-toolbar/undo.svg" aria-hidden="true" />
      </WithReset>
    </SudoTooltip>
  );
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isManuscriptDocumentToolName(name: unknown): boolean {
  return name === "manage_project_documents" || name === "apply_manuscript_edits";
}

function resolveToolExecutionOutcomeSummary(execution: {
  summary?: unknown;
  result?: unknown;
}): string | undefined {
  if (typeof execution.summary === "string" && execution.summary.trim().length > 0) {
    return execution.summary.trim();
  }
  const result = execution.result;
  if (!result || typeof result !== "object") return undefined;
  const resultRecord = result as { summary?: unknown; error?: unknown };
  if (typeof resultRecord.summary === "string" && resultRecord.summary.trim().length > 0) {
    return resultRecord.summary.trim();
  }
  if (typeof resultRecord.error === "string" && resultRecord.error.trim().length > 0) {
    return `error: ${resultRecord.error.trim()}`;
  }
  return undefined;
}

function manageProjectDocumentsSummaryLooksSuccessful(summary: unknown): boolean {
  return typeof summary === "string" && manageProjectDocumentsSummaryIndicatesManuscriptApplied(summary);
}

function findLatestManageProjectDocumentsExecution(
  rawToolExecutions: unknown,
  predicate?: (args: ManageProjectDocumentsArgs) => boolean,
): { args: ManageProjectDocumentsArgs; summary?: unknown } | undefined {
  if (!Array.isArray(rawToolExecutions)) return undefined;
  for (let i = rawToolExecutions.length - 1; i >= 0; i--) {
    const execution = rawToolExecutions[i];
    if (!execution || typeof execution !== "object") continue;
    const name = (execution as { name?: unknown }).name;
    if (!isManuscriptDocumentToolName(name)) continue;
    const args = (execution as { arguments?: unknown }).arguments;
    if (!args || typeof args !== "object") continue;
    const parsedArgs = args as ManageProjectDocumentsArgs;
    if (!predicate || predicate(parsedArgs)) {
      return {
        args: parsedArgs,
        summary: resolveToolExecutionOutcomeSummary(execution as { summary?: unknown; result?: unknown }),
      };
    }
  }
  return undefined;
}

function findLatestManageProjectDocumentsArgs(
  rawToolExecutions: unknown,
  predicate?: (args: ManageProjectDocumentsArgs) => boolean,
): ManageProjectDocumentsArgs | undefined {
  return findLatestManageProjectDocumentsExecution(rawToolExecutions, predicate)?.args;
}

function hasMarkerEditsWithNewText(markerEdits: unknown): boolean {
  if (!Array.isArray(markerEdits)) return false;
  return markerEdits.some((item) => {
    if (!item || typeof item !== "object") return false;
    return isNonEmptyString((item as { new_text?: unknown }).new_text);
  });
}

function extractInsertedPassageFromToolExecutions(rawToolExecutions: unknown): string | undefined {
  const editDocumentExecution = findLatestManageProjectDocumentsExecution(
    rawToolExecutions,
    (toolArgs) => toolArgs.operation === "edit_document",
  );
  if (
    editDocumentExecution
    && manageProjectDocumentsSummaryLooksSuccessful(editDocumentExecution.summary)
  ) {
    const fromEdit = extractInsertedTextFromEditDocumentArgs(editDocumentExecution.args);
    if (fromEdit) return fromEdit;
  }

  const reviewExecution = findLatestManageProjectDocumentsExecution(
    rawToolExecutions,
    (toolArgs) => toolArgs.operation === "review_edit_document",
  );
  if (reviewExecution && manageProjectDocumentsSummaryLooksSuccessful(reviewExecution.summary)) {
    if (hasMarkerEditsWithNewText(reviewExecution.args.marker_edits)) {
      const edits = reviewExecution.args.marker_edits as Array<{ new_text?: unknown }>;
      const last = [...edits].reverse().find((item) => isNonEmptyString(item?.new_text));
      if (last && isNonEmptyString(last.new_text)) return last.new_text.trim();
    }
  }

  return undefined;
}

function extractInsertedTextFromEditDocumentArgs(args: ManageProjectDocumentsArgs): string | undefined {
  const edit = args.edit;
  if (!edit || typeof edit !== "object") return undefined;
  const mode = (edit as { mode?: unknown }).mode;
  if (mode === "replace") {
    const newText = (edit as { new_text?: unknown }).new_text;
    return typeof newText === "string" && newText.trim().length > 0 ? newText : undefined;
  }
  if (mode === "replace_all") {
    const content = (edit as { content?: unknown }).content;
    return typeof content === "string" && content.trim().length > 0 ? content : undefined;
  }
  if (mode === "append" || mode === "prepend") {
    const text = (edit as { text?: unknown }).text;
    return typeof text === "string" && text.trim().length > 0 ? text : undefined;
  }
  return undefined;
}

function hasCreateDocumentWithInitialContent(rawToolExecutions: unknown): boolean {
  const execution = findLatestManageProjectDocumentsExecution(
    rawToolExecutions,
    (toolArgs) => toolArgs.operation === "create_document",
  );
  if (!execution || !manageProjectDocumentsSummaryLooksSuccessful(execution.summary)) return false;
  const initialContent = execution.args.initial_content;
  return typeof initialContent === "string" && initialContent.trim().length > 0;
}

function hasEditDocumentWithInsertedContent(rawToolExecutions: unknown): boolean {
  const insertedPassage = extractInsertedPassageFromToolExecutions(rawToolExecutions);
  return typeof insertedPassage === "string" && insertedPassage.trim().length > 0;
}

function hasReviewEditDocumentWithInsertedContent(rawToolExecutions: unknown): boolean {
  const execution = findLatestManageProjectDocumentsExecution(
    rawToolExecutions,
    (toolArgs) => toolArgs.operation === "review_edit_document",
  );
  if (!execution || !manageProjectDocumentsSummaryLooksSuccessful(execution.summary)) return false;
  if (hasMarkerEditsWithNewText(execution.args.marker_edits)) return true;
  return isNonEmptyString(execution.args.review_instruction);
}

function hasEditDocumentBatchWithInsertedContent(rawToolExecutions: unknown): boolean {
  const execution = findLatestManageProjectDocumentsExecution(
    rawToolExecutions,
    (toolArgs) => toolArgs.operation === "edit_document_batch",
  );
  if (!execution || !manageProjectDocumentsSummaryLooksSuccessful(execution.summary)) return false;
  return hasMarkerEditsWithNewText(execution.args.edits);
}

function hasManuscriptTextEditInToolExecutions(rawToolExecutions: unknown): boolean {
  if (Array.isArray(rawToolExecutions) && hasSuccessfulSelectionRewriteManuscriptApply(rawToolExecutions)) {
    return true;
  }
  return (
    hasCreateDocumentWithInitialContent(rawToolExecutions)
    || hasEditDocumentWithInsertedContent(rawToolExecutions)
    || hasReviewEditDocumentWithInsertedContent(rawToolExecutions)
    || hasEditDocumentBatchWithInsertedContent(rawToolExecutions)
  );
}

function thinkingTaskIsManuscriptWrite(task: {
  isWrite?: boolean;
  toolName?: string;
  elementType?: string;
  icon?: string;
  title?: string;
}): boolean {
  if (!task?.isWrite) return false;
  const toolName = String(task.toolName ?? "").toLowerCase();
  if (
    toolName === "generate_write_button_prose"
    || toolName === "manage_project_documents"
    || toolName === "apply_manuscript_edits"
    || toolName === "manage_draft_scenes"
    || isManuscriptStagingToolName(toolName)
  ) {
    return true;
  }
  if (task.elementType === "document" || task.icon === "book") return true;
  const title = String(task.title ?? "").toLowerCase();
  return /\b(writ(?:e|ing)|rewrite|draft|prose|manuscript|document|chapter|scene)\b/.test(title);
}

export const PersistentChatMessage: React.FC<Props> = memo((props) => {
  const [renderAttempt, setRenderAttempt] = useState(0);
  if (props.message.role !== "assistant") return <PersistentChatMessageContent {...props} />;

  // Temporary replies reuse "tbd". A new turn or completed stream can recover
  // a failed render without remounting healthy reply controls on every update.
  const resetKey = JSON.stringify([
    props.message.chat_id,
    props.message.id,
    props.message.metadata?.triggerUserMessageId,
    !!props.streaming,
    renderAttempt,
  ]);

  return (
    <ChatErrorBoundary
      resetKey={resetKey}
      fallback={
        <div role="alert" className="px-1 py-3 text-sm text-app-grey-1">
          <p>This reply couldn't be displayed. You can continue chatting.</p>
          <button type="button" className="mt-2 underline" onClick={() => setRenderAttempt((attempt) => attempt + 1)}>
            Try displaying again
          </button>
        </div>
      }
    >
      <PersistentChatMessageContent {...props} />
    </ChatErrorBoundary>
  );
});

const PersistentChatMessageContent: React.FC<Props> = memo(
  ({
    message,
    showInlineTerminalPlan = true,
    showInlineTerminalCommenting = true,
    searchedDocs,
    lookingAtData,
    streaming,
    cutoffData,
    contextWarning,
    toolRuns,
    outlineUpdates,
    characterUpdates,
    worldbuildingUpdates,
    metaUpdates,
    thinkingSteps,
    revertOption,
    revertTargetAssistantMessageId,
    canShowInlineEditForUserMessageId,
    onEditTailUserMessage,
    inlineEditUserMessageId,
    onCancelInlineEditUserMessage,
    onSubmitInlineEditUserMessage,
    isLastMessage,
    isLastAssistantMessage,
    isLastRevertibleAssistantMessage,
    isRevertedStoryBibleChange = false,
    onRevertStoryBibleSuccess,
    streamingModel,
    streamingWritePovTenseChiclets,
    streamingContextElements,
    streamingMentionedEntities,
    mode,
    initialRating,
    questionAnswer,
    ctaAction,
    onCtaGo,
    onCtaCancel,
    showResendLastMessage,
    onResendLastMessage,
    onDuplicatePlanRetry,
    activeDocumentId,
    activeProjectId,
    onRetryCommentCreation,
    feedbackByMessageId,
    onFeedbackTryAgain,
    onFeedbackSwitchToChatMode,
    onFeedbackStartFreshChat,
    userBranchSiblingIndex,
    userBranchSiblingCount,
    onUserBranchPrev,
    onUserBranchNext,
  }) => {
    const assistantContentRef = useRef<HTMLDivElement | null>(null);
    const tiptap = useCommentStore((state: any) => state.tiptapEditor);
    const comments = useCommentStore((state: any) => state.comments);
    const setToast = useToast()?.setToast;
    const [storyBibleRevertInFlight, setStoryBibleRevertInFlight] = useState(false);
    const { messages, chatId, projectId: chatProjectId, refetch } = usePersistentChat();
    const currentTurnUserMessageId = usePersistentChatStore((s) => s.currentTurnUserMessageId);
    const messageContent = useMemo(() => normalizeMessageContent(message.content), [message.content]);
    const streamingStoryBibleEntity = usePersistentChatStore((s) => {
      const entity = s.streamingStoryBibleGeneratedEntity;
      if (!isLastAssistantMessage || !entity) return null;
      if (message.id === "tbd") return entity;
      const persisted = parseStoryBibleGeneratedEntity(
        (message.metadata as { storyBibleGeneratedEntity?: unknown } | undefined)?.storyBibleGeneratedEntity,
      );
      if (persisted) return null;
      if (s.streaming || s.pendingAgentResponse) return null;
      // Handoff entity is shown on the live StreamingMessage row (id "tbd") only.
      return null;
    });
    const persistedStoryBibleEntity = useMemo(
      () => parseStoryBibleGeneratedEntity(
        (message.metadata as { storyBibleGeneratedEntity?: unknown } | undefined)?.storyBibleGeneratedEntity,
      ),
      [message.metadata],
    );
    const storyBibleGeneratedEntity = persistedStoryBibleEntity ?? streamingStoryBibleEntity;
    const activeStoryBibleCta = usePersistentChatStore((s) => {
      const cta = s.ctaAction;
      if (!cta || !isStoryBibleCtaType(cta.type)) return false;
      return cta.status === "in_progress" || cta.status === "awaiting_input";
    });
    const showStreamingSparkle = streaming && !storyBibleGeneratedEntity && !activeStoryBibleCta;

    const handleAssistantCopy = useCallback(async () => {
      const fallbackText = extractPlainTextFromMentions(messageContent);
      const html = assistantContentRef.current?.innerHTML ?? (messageContent ? messageContent.replace(/\n/g, "<br />") : "");
      const plainText = derivePlainTextFromHtml(html, fallbackText);
      await copyHtmlToClipboard(html, plainText);
    }, [messageContent]);

    const markdownComponents = useMemo(
      () => createChatMarkdownComponents({ activeDocumentId, activeProjectId, variant: "assistant" }),
      [activeDocumentId, activeProjectId],
    );

    const assistantMarkdownSource = useMemo(() => {
      const raw = messageContent.trim();
      const withoutThinkTags = stripThinkTagsFromAssistantOutput(raw);
      const normalized = normalizeParentheticalCommentLinks(withoutThinkTags);
      const tightenedLists = tightenMarkdownListSpacing(normalized);
      if (!streaming) return tightenedLists;
      return hideIncompleteMarkdownLinksAtEnd(tightenedLists);
    }, [messageContent, streaming]);
    const insertedPassage = useMemo(
      () => extractInsertedPassageFromToolExecutions((message.metadata as { toolExecutions?: unknown })?.toolExecutions),
      [message.metadata],
    );
    const visibleThinkingSteps = useMemo(() => {
      if (!Array.isArray(thinkingSteps) || thinkingSteps.length === 0) return thinkingSteps;
      if (mode !== "chat") return thinkingSteps;
      return thinkingSteps
        .map((step) => ({
          ...step,
          tasks: step.tasks.filter((task) => !isManuscriptStagingToolName(task.toolName)),
        }))
        .filter((step) => step.tasks.length > 0);
    }, [thinkingSteps, mode]);
    const hasManuscriptWriteThinkingTask = useMemo(
      () => Array.isArray(visibleThinkingSteps) && visibleThinkingSteps.some((step) =>
        Array.isArray(step?.tasks) && step.tasks.some((task: any) => thinkingTaskIsManuscriptWrite(task)),
      ),
      [visibleThinkingSteps],
    );
    const persistedWriteToolExecution = useMemo(() => {
      const toolExecutions = (message.metadata as { toolExecutions?: unknown })?.toolExecutions;
      if (!Array.isArray(toolExecutions)) return undefined;
      for (let i = toolExecutions.length - 1; i >= 0; i--) {
        const execution = toolExecutions[i];
        if (!execution || typeof execution !== "object") continue;
        if ((execution as { name?: unknown }).name !== "generate_write_button_prose") continue;
        const result = (execution as { result?: unknown }).result;
        const resultRecord =
          result && typeof result === "object" ? (result as Record<string, unknown>) : undefined;
        const model =
          (typeof (execution as { model?: unknown }).model === "string"
            ? (execution as { model?: string }).model
            : undefined)
          ?? (typeof resultRecord?.model === "string" ? resultRecord.model : undefined);
        const writePovTenseChiclets =
          parseWritePovTenseChiclets((execution as { writePovTenseChiclets?: unknown }).writePovTenseChiclets)
          ?? parseWritePovTenseChiclets(resultRecord?.writePovTenseChiclets);
        if ((typeof model === "string" && model.trim()) || writePovTenseChiclets) {
          return {
            model,
            numOptions:
              typeof (execution as { numOptions?: unknown }).numOptions === "number"
                ? (execution as { numOptions: number }).numOptions
                : typeof resultRecord?.numOptions === "number"
                  ? resultRecord.numOptions
                  : undefined,
            wordLength:
              typeof (execution as { wordLength?: unknown }).wordLength === "number"
                || (execution as { wordLength?: unknown }).wordLength === null
                ? (execution as { wordLength?: number | null }).wordLength
                : typeof resultRecord?.wordLength === "number" || resultRecord?.wordLength === null
                  ? (resultRecord.wordLength as number | null)
                  : undefined,
            writePovTenseChiclets,
          };
        }
      }
      return undefined;
    }, [message.metadata]);
    const triggerUserMessage = useMemo(() => {
      const triggerUserMessageId = message.metadata?.triggerUserMessageId;
      if (typeof triggerUserMessageId !== "string" || !triggerUserMessageId.trim()) return undefined;
      return messages.find((entry) => entry.id === triggerUserMessageId);
    }, [message.metadata?.triggerUserMessageId, messages]);
    const triggerUserWriteButtonSettings = useMemo(
      () => parseAgenticWriteButtonSettingsFromTurnContext(triggerUserMessage?.metadata?.turnContext),
      [triggerUserMessage?.metadata?.turnContext],
    );
    const triggerUserGuidance = useMemo(
      () => resolveUserGuidanceForWriteChiclets({
        messages,
        triggerUserMessageId: message.metadata?.triggerUserMessageId,
        currentTurnUserMessageId,
      }),
      [messages, message.metadata?.triggerUserMessageId, currentTurnUserMessageId],
    );
    const assistantThinkingStepWriteSettings = useMemo(() => {
      if (!hasManuscriptWriteThinkingTask) return undefined;
      const turnContextSettings =
        triggerUserWriteButtonSettings
        ?? (streaming ? buildAgenticWriteButtonSettingsFromEditor() : undefined);
      return resolveWriteSettingsForThinkingChiclets({
        persisted: persistedWriteToolExecution,
        turnContextSettings,
        userGuidance: triggerUserGuidance,
        highlightedPassage: message.metadata?.passage as string | undefined,
      });
    }, [
      hasManuscriptWriteThinkingTask,
      persistedWriteToolExecution,
      triggerUserWriteButtonSettings,
      triggerUserGuidance,
      message.metadata?.passage,
      streaming,
    ]);
    const assistantThinkingStepModel = useMemo(() => {
      if (!hasManuscriptWriteThinkingTask) return undefined;
      const turnContextSettings =
        triggerUserWriteButtonSettings
        ?? (streaming ? buildAgenticWriteButtonSettingsFromEditor() : undefined);
      return resolveWriteModelForThinkingChiclet({
        persistedModel: persistedWriteToolExecution?.model,
        turnContextSettings,
      });
    }, [hasManuscriptWriteThinkingTask, persistedWriteToolExecution, triggerUserWriteButtonSettings, streaming]);
    const assistantThinkingStepWritePovTenseChiclets = useMemo(() => {
      if (!hasManuscriptWriteThinkingTask) return undefined;
      if (streaming && streamingWritePovTenseChiclets?.length) {
        return streamingWritePovTenseChiclets;
      }
      return parseWritePovTenseChiclets(persistedWriteToolExecution?.writePovTenseChiclets);
    }, [
      hasManuscriptWriteThinkingTask,
      persistedWriteToolExecution,
      streaming,
      streamingWritePovTenseChiclets,
    ]);
    const hasDocumentTextEdit = useMemo(() => {
      const toolExecutions = (message.metadata as { toolExecutions?: unknown })?.toolExecutions;
      return hasManuscriptTextEditInToolExecutions(toolExecutions);
    }, [message.metadata]);
    /** Cleared when chat undo succeeds — persisted tool metadata still lists an edit otherwise. */
    const [manuscriptEditUndone, setManuscriptEditUndone] = useState(false);
    useEffect(() => {
      setManuscriptEditUndone(false);
    }, [message.id]);
    const showDocumentTextEditAccept = hasDocumentTextEdit && !manuscriptEditUndone;
    const isTerminalPlanRunning = useMemo(() => {
      const rawPlan = message.metadata?.plan as PlanData | undefined;
      if (!rawPlan) return false;
      const inlinePlan = normalizeTerminalPlanStatus(rawPlan);
      return inlinePlan.status === "in_progress" || inlinePlan.status === "cancelling";
    }, [message.metadata?.plan]);

    // Saved draft history survives declining insertion regardless of runtime.
    const rewriteOptions = useMemo(() => parseRewriteOptions(message.metadata?.rewriteOptions), [message.metadata]);
    const hasStructuredRewritePresentation = message.metadata?.rewritePresentationVersion === 1 || Boolean(rewriteOptions);
    const manuscriptChangeMeta = useMemo(() => {
      const metadata = (message.metadata ?? {}) as Record<string, unknown>;
      return metadata.manuscriptChange;
    }, [message.metadata]);
    const [localManuscriptChangeStatus, setLocalManuscriptChangeStatus] = useState<string | null>(null);
    const [carouselAppliedOptionIndex, setCarouselAppliedOptionIndex] = useState<number | null>(null);
    useEffect(() => {
      setLocalManuscriptChangeStatus(null);
      setCarouselAppliedOptionIndex(null);
    }, [message.id]);
    useEffect(() => {
      if (!localManuscriptChangeStatus) return;
      const serverRef = parseManuscriptChangeMessageRef(manuscriptChangeMeta);
      if (serverRef?.status === localManuscriptChangeStatus) {
        setLocalManuscriptChangeStatus(null);
      }
    }, [localManuscriptChangeStatus, manuscriptChangeMeta]);
    const effectiveManuscriptChangeMeta = useMemo(() => {
      if (!localManuscriptChangeStatus) return manuscriptChangeMeta;
      if (!manuscriptChangeMeta || typeof manuscriptChangeMeta !== "object" || Array.isArray(manuscriptChangeMeta)) {
        return manuscriptChangeMeta;
      }
      return {
        ...manuscriptChangeMeta,
        status: localManuscriptChangeStatus,
      };
    }, [localManuscriptChangeStatus, manuscriptChangeMeta]);

    const typedManuscriptChangeRef = useMemo(
      () => parseManuscriptChangeMessageRef(effectiveManuscriptChangeMeta),
      [effectiveManuscriptChangeMeta],
    );
    const showWholeScopeResult = manuscriptChangeUsesCompactResult(effectiveManuscriptChangeMeta, true);
    const showLiteralReplacementResult = showWholeScopeResult || Boolean(
      typedManuscriptChangeRef?.preview?.literalReplacements?.length
      && !typedManuscriptChangeRef.preview.replacementText
      && !(typedManuscriptChangeRef.options ?? []).some(option => option.suggestionText || option.preview?.replacementText)
      && (typedManuscriptChangeRef.requestedOptionCount ?? 1) === 1
      && (typedManuscriptChangeRef.options ?? []).every((option) => option.preview?.literalReplacements?.length),
    );
    const persistedAppliedSuggestionIndex = useMemo(
      () => manuscriptChangeAppliedSuggestionIndex(
        effectiveManuscriptChangeMeta,
        (message.metadata?.rewriteSuggestions as { suggestions?: unknown } | undefined)?.suggestions,
      ),
      [effectiveManuscriptChangeMeta, message.metadata?.rewriteSuggestions],
    );
    const effectiveAppliedSuggestionIndex =
      carouselAppliedOptionIndex ?? persistedAppliedSuggestionIndex;
    const allowManuscriptChangeApply =
      !manuscriptChangeIsReadOnlyHistory(typedManuscriptChangeRef)
      && typedManuscriptChangeRef?.status !== "preview_only"
      && !manuscriptChangeStatusShowsDurableUndo(typedManuscriptChangeRef?.status ?? "");
    useEffect(() => {
      if (
        typedManuscriptChangeRef?.status !== "generating"
        || !typedManuscriptChangeRef.changeSetId
        || !refetch
      ) return undefined;
      let polls = 0;
      const poll = () => {
        polls += 1;
        refetch();
        if (polls >= 150) clearInterval(interval);
      };
      const interval = window.setInterval(poll, 2_000);
      poll();
      return () => window.clearInterval(interval);
    }, [refetch, typedManuscriptChangeRef?.changeSetId, typedManuscriptChangeRef?.status]);
    const [manuscriptChangeOutcomeMessage, setManuscriptChangeOutcomeMessage] = useState<string | null>(null);
    useEffect(() => {
      setManuscriptChangeOutcomeMessage(null);
    }, [message.id]);
    const assistantMarkdownForDisplay = useMemo(() => {
      if (!typedManuscriptChangeRef?.preview) return assistantMarkdownSource;
      return stripRedundantManuscriptChangeBeforeAfterProse(assistantMarkdownSource);
    }, [assistantMarkdownSource, typedManuscriptChangeRef]);
    const showTypedManuscriptChangeCarousel =
      carouselAppliedOptionIndex != null
      || manuscriptChangeCanUseRewriteCarousel(effectiveManuscriptChangeMeta, true);
    // Typed manuscript changes own apply/undo. After apply, persisted rewrite
    // cards must not come back as a local-editor Apply path.
    const streamingProsePreviewState = usePersistentChatStore((state) => {
      if (!isLastAssistantMessage || message.role !== "assistant" || messageHasFinalManuscriptChange(message)) return "";
      const ownsTurn = message.id === "tbd" || Boolean(state.currentTurnUserMessageId
        && message.metadata?.triggerUserMessageId === state.currentTurnUserMessageId)
        || Boolean(state.completedPlanSummaryId && message.metadata?.planExecution === true
          && message.metadata?.plan?.id === state.completedPlanSummaryId);
      if (!ownsTurn) return "";
      return Object.keys(state.currentProsePreviews ?? {}).length
        ? state.currentProsePreviews
        : state.currentProsePreview;
    });
    const streamingProsePreviews = useMemo(() => {
      if (typeof streamingProsePreviewState === "string") {
        return streamingProsePreviewState ? [streamingProsePreviewState] : [];
      }
      // The store retains each generation in arrival order until it is reset or finalized.
      return Object.values(streamingProsePreviewState);
    }, [streamingProsePreviewState]);
    const streamedExplanations = usePersistentChatStore(state => state.currentProsePreviewExplanations);
    const streamingExplanations = typeof streamingProsePreviewState === "object"
      ? Object.keys(streamingProsePreviewState).map(id => streamedExplanations?.[id] ?? "") : [];
    const streamedGroups = usePersistentChatStore(state => state.currentProsePreviewGroups);
    const streamingPreviewGroups = useMemo(() => {
      const groups = new Map<string, { id: string; title: string; suggestions: string[]; explanations: string[] }>();
      const entries = typeof streamingProsePreviewState === "object"
        ? Object.entries(streamingProsePreviewState) : streamingProsePreviewState ? [["legacy", streamingProsePreviewState]] : [];
      for (const [id, text] of entries) {
        const metadata = streamedGroups?.[id];
        const key = metadata?.id ?? "legacy";
        const group = groups.get(key) ?? { id: key, title: metadata?.title ?? "", suggestions: [], explanations: [] };
        group.title = metadata?.title || group.title;
        group.suggestions.push(text);
        group.explanations.push(streamedExplanations?.[id] ?? "");
        groups.set(key, group);
      }
      return [...groups.values()];
    }, [streamingProsePreviewState, streamedGroups, streamedExplanations]);
    const showRewriteSuggestionCarousel = !showLiteralReplacementResult && (
      streamingProsePreviews.length > 0 ||
      showTypedManuscriptChangeCarousel
      || (
        !typedManuscriptChangeRef
        && messageHasRewriteSuggestionCarouselMetadata(message)
      ));

    const manuscriptChangeOwnership = useMemo(() => {
      // Applied change-set refs still own durable Undo, including chat-apply
      // confirmation messages that re-render the suggestion cards.
      if (!typedManuscriptChangeRef) return null;
      const metadata = (message.metadata ?? {}) as Record<string, unknown>;
      const changeId =
        manuscriptChangeMeta
        && typeof manuscriptChangeMeta === "object"
        && !Array.isArray(manuscriptChangeMeta)
        && typeof (manuscriptChangeMeta as { changeId?: unknown }).changeId === "string"
          ? String((manuscriptChangeMeta as { changeId: string }).changeId).trim()
          : "";
      const documentId = resolveManuscriptChangeDocumentId({
        messageMetadata: metadata,
        activeDocumentId,
      });
      const projectId =
        (typeof activeProjectId === "string" && activeProjectId.trim())
        || (typeof chatProjectId === "string" && chatProjectId.trim())
        || (typeof metadata.project_id === "string" && metadata.project_id.trim())
        || "";
      const resolvedChatId =
        (typeof chatId === "string" && chatId.trim())
        || (typeof message.chat_id === "string" && message.chat_id.trim())
        || "";
      if (!changeId || !documentId || !projectId || !resolvedChatId) return null;
      return {
        changeId,
        chatId: resolvedChatId,
        projectId,
        documentId,
      };
    }, [
      activeDocumentId,
      activeProjectId,
      chatId,
      chatProjectId,
      manuscriptChangeMeta,
      message.chat_id,
      message.metadata,
      typedManuscriptChangeRef,
    ]);

    const manuscriptChangeOperation = useMemo(
      () => extractProposeManuscriptChangeOperation(
        (message.metadata as { toolExecutions?: unknown } | undefined)?.toolExecutions,
      ),
      [message.metadata],
    );

    const typedManuscriptChangeSuggestions = useMemo(() => {
      if (!showTypedManuscriptChangeCarousel) return null;
      const entries = manuscriptChangeSuggestionEntries({
        manuscriptChange: effectiveManuscriptChangeMeta,
        includeReadOnlyHistory: true,
      });
      const suggestions = manuscriptChangeSuggestionTexts({
        manuscriptChange: effectiveManuscriptChangeMeta,
        operation: manuscriptChangeOperation,
        includeReadOnlyHistory: true,
      });
      return suggestions.length > 0 ? { intro: "", suggestions, entries } : null;
    }, [
      effectiveManuscriptChangeMeta,
      manuscriptChangeOperation,
      showTypedManuscriptChangeCarousel,
    ]);

    const structuredRewriteSuggestions = useMemo(() => {
      if (!showRewriteSuggestionCarousel) return null;
      if (rewriteOptions) return { intro: rewriteOptions.intro ?? '',
        suggestions: rewriteOptions.options.map(option => option.text), entries: undefined };
      const parsedManuscriptChange = parseManuscriptChangeMessageRef(effectiveManuscriptChangeMeta);
      const hasTypedOptionProse = Boolean(
        parsedManuscriptChange?.options?.some((option) =>
          option.suggestionText?.trim()
          || option.preview?.replacementText?.trim()
          || option.preview?.after?.trim(),
        ),
      );
      if (hasTypedOptionProse && typedManuscriptChangeSuggestions?.suggestions.length) {
        return typedManuscriptChangeSuggestions;
      }
      const metadata = (message.metadata ?? {}) as Record<string, unknown>;
      const raw = metadata.rewriteSuggestions;
      if (raw !== null && typeof raw === "object" && !Array.isArray(raw)) {
        const rec = raw as Record<string, unknown>;
        const intro = typeof rec.intro === "string" ? rec.intro : "";
        const suggestions = normalizeManuscriptChangeSuggestions(rec.suggestions, null);
        if (suggestions) {
          const entries = manuscriptChangeSuggestionEntries({
            manuscriptChange: effectiveManuscriptChangeMeta,
            rewriteSuggestions: rec.suggestions,
            includeReadOnlyHistory: true,
          });
          return { intro, suggestions, entries };
        }
      }
      return typedManuscriptChangeSuggestions;
    }, [
      effectiveManuscriptChangeMeta,
      message.metadata,
      showRewriteSuggestionCarousel,
      typedManuscriptChangeSuggestions,
      rewriteOptions,
    ]);

    const rewriteSuggestionsFromToolExecutions = useMemo(() => {
      if (!showRewriteSuggestionCarousel) return null;
      return extractRewriteSuggestionsFromPersistedToolExecutions(message.metadata?.toolExecutions);
    }, [
      showRewriteSuggestionCarousel,
      message.metadata?.toolExecutions,
    ]);

    const shouldParseRewriteSuggestionsFromText = false;

    const rewriteSuggestions = useMemo(() => {
      if (!showRewriteSuggestionCarousel) return null;
      if (structuredRewriteSuggestions) return structuredRewriteSuggestions;
      if (rewriteSuggestionsFromToolExecutions) return rewriteSuggestionsFromToolExecutions;
      if (streamingProsePreviews.length > 0) return { intro: "", suggestions: streamingProsePreviews, entries: undefined };
      if (!shouldParseRewriteSuggestionsFromText) return null;
      return parseRewriteSuggestionsFromText(assistantMarkdownSource);
    }, [
      assistantMarkdownSource,
      rewriteSuggestionsFromToolExecutions,
      streamingProsePreviews,
      shouldParseRewriteSuggestionsFromText,
      showRewriteSuggestionCarousel,
      structuredRewriteSuggestions,
    ]);
    const isProvisionalSuggestion = Boolean(streamingProsePreviews.length > 0 && !structuredRewriteSuggestions && !rewriteSuggestionsFromToolExecutions);
    const hasRewriteSuggestionCarousel = useMemo(() => {
      if (!showRewriteSuggestionCarousel) return false;
      if (rewriteSuggestions) return true;
      if (!Array.isArray(message.metadata?.toolExecutions)) return false;
      return message.metadata.toolExecutions.some((toolExecution: any) =>
        toolResultAllowsCarouselRender({
          toolName: toolExecution?.name,
          result: toolExecution?.result,
        }),
      );
    }, [message.metadata?.toolExecutions, rewriteSuggestions, showRewriteSuggestionCarousel]);
    const manuscriptChangeSuggestionPending = useMemo(() => {
      const hasRenderableCarousel = showLiteralReplacementResult
        || (hasRewriteSuggestionCarousel && Boolean(rewriteSuggestions?.suggestions?.length));
      if (showWholeScopeResult) return false;
      return manuscriptChangeSuggestionCardPending(
        effectiveManuscriptChangeMeta,
        hasRenderableCarousel,
      )
        || proposeManuscriptChangeToolAwaitingSuggestionCard(
          message.metadata?.toolExecutions,
          hasRenderableCarousel,
        )
        || (
          !hasRenderableCarousel
          && Boolean(streaming)
          && Boolean(isLastAssistantMessage)
          && Array.isArray(toolRuns)
          && toolRuns.some((run) =>
            run.status === "running" && isManuscriptStagingToolName(run.name),
          )
        );
    }, [
      effectiveManuscriptChangeMeta,
      showWholeScopeResult,
      hasRewriteSuggestionCarousel,
      showLiteralReplacementResult,
      isLastAssistantMessage,
      message.metadata?.toolExecutions,
      rewriteSuggestions?.suggestions?.length,
      streaming,
      toolRuns,
    ]);
    const writeSuggestionCardHeader = useMemo(
      () => ({ mode: "auto" as const }),
      [],
    );

    const previewGroups = useMemo(
      () => independentPreviewGroups(message.metadata?.toolExecutions, message.metadata),
      [message.metadata],
    );
    const orderedPreviews = useMemo(() => orderedPreviewPresentation(message.metadata, previewGroups, assistantMarkdownForDisplay), [message.metadata, previewGroups, assistantMarkdownForDisplay]);
    const textBelowPreviewGroups = useMemo(
      () => textWithoutPreviewGroupSuggestions(assistantMarkdownForDisplay, previewGroups),
      [assistantMarkdownForDisplay, previewGroups],
    );
    const streamingPreserveChatTextWithSuggestions = usePersistentChatStore((s) =>
      Boolean(isLastAssistantMessage && message.id === "tbd" && s.currentPreserveChatTextWithSuggestions));
    const textBelowRewriteCarousel = useMemo(() => {
      if (!rewriteSuggestions || rewriteOptions) return null;
      if (message.metadata?.preserveChatTextWithSuggestions === true || streamingPreserveChatTextWithSuggestions) {
        return assistantMarkdownForDisplay.trim() || null;
      }
      return chatTextBelowRewriteCarousel(assistantMarkdownForDisplay);
    }, [assistantMarkdownForDisplay, rewriteSuggestions, rewriteOptions, message.metadata?.preserveChatTextWithSuggestions, streamingPreserveChatTextWithSuggestions]);

    /** Manuscript feedback type picker is self-explanatory; hide redundant assistant prose in the same turn. */
    const hideAssistantMarkdownForFeedbackTypesPicker = useMemo(() => {
      if (message.role !== "assistant" || !message.metadata?.questionAsked) return false;
      const uq = message.metadata.userQuestion;
      if (!uq || typeof uq !== "object") return false;
      const questions =
        Array.isArray(uq.questions) && uq.questions.length > 0
          ? uq.questions
          : typeof uq.question === "string"
            ? [{ question: uq.question, inputType: uq.inputType ?? "button", options: uq.options ?? [] }]
            : null;
      const firstQuestionText = questions?.[0]?.question;
      return typeof firstQuestionText === "string" && questionIsFeedbackTypesPicker(firstQuestionText);
    }, [message.role, message.metadata]);

    const showAssistantProseSection = useMemo(() => {
      if (previewGroups.length > 0) return true;
      if (hideAssistantMarkdownForFeedbackTypesPicker || storyBibleGeneratedEntity) return false;
      if (manuscriptChangeSuggestionPending) return false;
      if (manuscriptChangeOutcomeMessage) return true;
      if (hasRewriteSuggestionCarousel) {
        return Boolean(rewriteSuggestions?.suggestions?.length);
      }
      return Boolean(assistantMarkdownForDisplay.trim());
    }, [
      previewGroups.length,
      assistantMarkdownForDisplay,
      hasRewriteSuggestionCarousel,
      hideAssistantMarkdownForFeedbackTypesPicker,
      manuscriptChangeOutcomeMessage,
      manuscriptChangeSuggestionPending,
      rewriteSuggestions?.suggestions?.length,
      storyBibleGeneratedEntity,
    ]);

    const typedApprovalInFlightRef = useRef(false);
    const performDurableManuscriptUndo = useCallback(async (showReceipt: boolean) => {
      if (!typedManuscriptChangeRef?.changeSetId || !manuscriptChangeOwnership) return false;
      const result = await undoManuscriptChangeSet({
        changeSetId: typedManuscriptChangeRef.changeSetId,
        chatId: manuscriptChangeOwnership.chatId,
        projectId: manuscriptChangeOwnership.projectId,
      });
      const undone = result.status === "undone";
      const compactResultOwnsReceipt = showLiteralReplacementResult
        && ["undone", "partially_undone", "undoing"].includes(result.status);
      if ((showReceipt || !undone) && (!compactResultOwnsReceipt || (!undone && result.message))) {
        setManuscriptChangeOutcomeMessage(
          result.message ?? (undone
            ? "I undid that change."
            : "I couldn’t safely undo that change."),
        );
      }
      if (compactResultOwnsReceipt && (undone || !result.message)) setManuscriptChangeOutcomeMessage(null);
      if (undone || manuscriptChangeStatusShowsDurableUndo(result.status)) {
        setLocalManuscriptChangeStatus(result.status);
      }
      if (undone) setCarouselAppliedOptionIndex(null);
      refetch?.();
      return undone;
    }, [manuscriptChangeOwnership, refetch, showLiteralReplacementResult, typedManuscriptChangeRef]);
    const handleDurableManuscriptUndo = useCallback(
      () => performDurableManuscriptUndo(true),
      [performDurableManuscriptUndo],
    );
    const handleCarouselManuscriptUndo = useCallback(
      () => performDurableManuscriptUndo(false),
      [performDurableManuscriptUndo],
    );
    const handleApplyRewriteSuggestion = useCallback((suggestionText: string, optionIndex = 0, renderedSuggestionHtml?: string) => {
      if (hasStructuredRewritePresentation && (!rewriteOptions || !rewriteOptionsCanApply(rewriteOptions, effectiveManuscriptChangeMeta))) return;
      const metadata = (message.metadata ?? {}) as Record<string, unknown>;
      const triggerUserMessageId = typeof metadata.triggerUserMessageId === "string"
        ? metadata.triggerUserMessageId
        : "";
      const turnContext = parseRewriteTurnContext(
        triggerUserMessageId
          ? messages.find((entry) => entry.id === triggerUserMessageId)?.metadata?.turnContext
          : undefined,
      );
      const editorSelectionHintForApprove = (
        metadata.editorSelectionHint
        ?? turnContext?.editorSelectionHint
      ) as RewriteEditorSelectionHint | undefined;

      const runLocalRewriteApply = () => {
        if (!tiptap) return;
        const effectiveRenderedSuggestionHtml =
          renderedSuggestionHtml ?? renderMarkdownForTiptapInsert(suggestionText);

        const relativeRewriteTarget = inferRelativeRewriteTarget(rewriteSuggestions?.intro ?? "");
        const referencedCommentId = typeof metadata.referencedCommentId === "string"
          ? metadata.referencedCommentId
          : "";
        const referencedComments = Array.isArray(metadata.referencedComments)
          ? metadata.referencedComments
          : [];
        const fallbackReferencedCommentId = referencedComments.find(
          (entry) => entry && typeof entry === "object" && typeof (entry as Record<string, unknown>).commentId === "string",
        ) as Record<string, unknown> | undefined;
        const resolvedCommentId = referencedCommentId || String(fallbackReferencedCommentId?.commentId || "");

        let applied = false;
        if (isEditorDocumentEffectivelyEmpty(tiptap)) {
          applied = applySuggestionToEmptyDocument(tiptap, suggestionText, {
            renderedHtml: effectiveRenderedSuggestionHtml,
          });
        }
        const activeHighlight = usePersistentChatStore.getState().activeHighlight?.trim() ?? "";
        const rewriteTargetText = String(
          relativeRewriteTarget
          || metadata.rewriteTargetText
          || metadata.passage
          || turnContext?.passage
          || activeHighlight
          || "",
        ).trim();
        let editorSelectionHint = (
          metadata.editorSelectionHint
          ?? turnContext?.editorSelectionHint
        ) as RewriteEditorSelectionHint | undefined;
        const documentId = resolveRewriteApplyDocumentId({
          turnContext,
          messageMetadata: metadata,
          activeDocumentId: activeDocumentId ?? undefined,
        });
        const rewriteAnchorTexts = [rewriteTargetText, activeHighlight].filter(
          (entry, index, all) => Boolean(entry) && all.indexOf(entry) === index,
        );
        const storedHint = editorSelectionHint;
        const storedHintStillValid = Boolean(
          documentId
          && storedEditorSelectionHintStillValid(tiptap, storedHint, rewriteAnchorTexts, documentId),
        );

        if (!applied && tiptap.state?.selection) {
          const { from, to } = tiptap.state.selection;
          if (to > from) {
            const liveSelectionText = tiptap.state.doc.textBetween(from, to, "\n", "\0");
            const matchesStoredPm = storedHintStillValid
              && from === storedHint?.pmFrom
              && to === storedHint?.pmTo;
            const matchesAnchor = rewriteAnchorTexts.some((anchor) =>
              passageMatchesDocRangeText(liveSelectionText, anchor),
            );
            if (matchesStoredPm || matchesAnchor) {
              applied = applySuggestionToSelectionInEditor(tiptap, suggestionText, {
                renderedHtml: effectiveRenderedSuggestionHtml,
              });
            }
          }
        }
        const hasActiveSelection = Boolean(
          tiptap.state?.selection && tiptap.state.selection.to > tiptap.state.selection.from,
        );

        if (!storedHintStillValid && rewriteTargetText && documentId) {
          const rebuiltHint = buildEditorSelectionHintForPassage(tiptap, {
            documentId,
            passage: rewriteTargetText,
            preferredHint: editorSelectionHint,
          });
          if (rebuiltHint) {
            editorSelectionHint = rebuiltHint;
          }
        } else if (storedHintStillValid && storedHint) {
          editorSelectionHint = storedHint;
        }
        const hadStructuredRewriteTarget = Boolean(rewriteTargetText || editorSelectionHint);
        const wouldReplaceEntireDocument = Boolean(
          rewriteTargetText
          && tiptap.state?.doc
          && passageMatchWouldReplaceEntireDocument(tiptap.state.doc, rewriteTargetText),
        );

        if (!applied && rewriteTargetText && !wouldReplaceEntireDocument) {
          applied = applySuggestionToPassageInEditor(tiptap, suggestionText, {
            passage: rewriteTargetText,
            renderedHtml: effectiveRenderedSuggestionHtml,
            editorSelectionHint,
          });
        }

        if (!applied && rewriteSuggestions?.suggestions?.length) {
          applied = applySuggestionReplacingSiblingCarouselOption(
            tiptap,
            suggestionText,
            rewriteSuggestions.suggestions,
            { renderedHtml: effectiveRenderedSuggestionHtml, editorSelectionHint },
          );
        }

        if (!applied && resolvedCommentId) {
          const matchedComment = Array.isArray(comments)
            ? comments.find((comment: any) => String(comment?.id) === resolvedCommentId)
            : null;
          const annotationId = matchedComment?.annotationId;
          if (annotationId) {
            applied = applySuggestionToEditor(tiptap, suggestionText, { annotationId, renderedHtml: effectiveRenderedSuggestionHtml });
          }
        }
        if (!applied) {
          applied = applySuggestionToSelectionInEditor(tiptap, suggestionText, { renderedHtml: effectiveRenderedSuggestionHtml });
        }
        if (!applied && (!hadStructuredRewriteTarget || !hasActiveSelection)) {
          applied = applySuggestionAtCursorInEditor(tiptap, suggestionText, { renderedHtml: effectiveRenderedSuggestionHtml });
        }
        if (!applied) {
          setToast?.("Could not apply rewrite — try scrolling to the passage or comment and try again.");
        }
      };

      if (
        (showTypedManuscriptChangeCarousel || showLiteralReplacementResult)
        && typedManuscriptChangeRef
        && manuscriptChangeOwnership
      ) {
        if (typedApprovalInFlightRef.current) return;
        typedApprovalInFlightRef.current = true;
        if (
          typedManuscriptChangeRef.changeSetId
          && typedManuscriptChangeRef.proposalGroupId
        ) {
          const selectedEntry = rewriteSuggestions && "entries" in rewriteSuggestions
            ? rewriteSuggestions.entries?.[optionIndex]
            : typedManuscriptChangeSuggestions?.entries[optionIndex];
          const selectedTypedOption = typedManuscriptChangeRef.options?.find(
            (option) => option.optionIndex === optionIndex,
          );
          void applyManuscriptChangeSet({
            changeSetId: typedManuscriptChangeRef.changeSetId,
            proposalGroupId: typedManuscriptChangeRef.proposalGroupId,
            optionId: selectedEntry?.optionId
              ?? selectedTypedOption?.changeId
              ?? typedManuscriptChangeRef.changeId,
            chatId: manuscriptChangeOwnership.chatId,
            projectId: manuscriptChangeOwnership.projectId,
          })
            .then(async (result) => {
              // A failed attempt need not change the persisted lifecycle. Let refetch
              // decide whether the proposal is still applyable instead of hiding Retry.
              if (showLiteralReplacementResult && result.status !== "could_not_apply") {
                setLocalManuscriptChangeStatus(result.status);
              }
              if (result.status === "applied" && selectedEntry) {
                dispatchAppliedManuscriptChangeHighlight(
                  selectedEntry.documentId ?? manuscriptChangeOwnership.documentId,
                  selectedEntry.text,
                );
              }
              if (manuscriptChangeStatusShowsDurableUndo(result.status)) {
                setCarouselAppliedOptionIndex(optionIndex);
              }
              if (result.status !== "applied") {
                setToast?.(
                  result.message
                  ?? (result.status === "partially_applied"
                    ? "I applied some changes, but couldn’t safely apply the rest."
                    : "I couldn’t apply that change. No document changes were made."),
                );
              }
              refetch?.();
            })
            .catch((error) => {
              Sentry.captureException(error, { tags: { feature: "manuscript_change_set_apply" } });
              setToast?.(safeManuscriptChangeApplyFailureMessage(error));
            })
            .finally(() => {
              typedApprovalInFlightRef.current = false;
            });
          return;
        }
        const selectedSavedOption = typedManuscriptChangeRef.options?.find(
          option => option.optionIndex === optionIndex,
        );
        void approveManuscriptChange({
          ...manuscriptChangeOwnership,
          ...(selectedSavedOption ? { changeId: selectedSavedOption.changeId,
            documentId: selectedSavedOption.documentId ?? manuscriptChangeOwnership.documentId } : {}),
          ...(editorSelectionHintForApprove?.documentId
            ? { editorSelectionHint: editorSelectionHintForApprove }
            : {}),
          ...(turnContext?.passage ? { clientPassage: turnContext.passage } : {}),
        })
          .then((result) => {
            if (result.outcome === "applied" || result.outcome === "visible") {
              if (typedManuscriptChangeRef?.changeSetId) {
                setLocalManuscriptChangeStatus(result.outcome === "visible" ? "visible" : "applied");
              }
              const previewAfter = selectedSavedOption?.preview?.replacementText
                ?? selectedSavedOption?.preview?.after ?? (
                manuscriptChangeMeta
                && typeof manuscriptChangeMeta === "object"
                && !Array.isArray(manuscriptChangeMeta)
                && manuscriptChangeMeta.preview
                && typeof manuscriptChangeMeta.preview === "object"
                && !Array.isArray(manuscriptChangeMeta.preview)
                && typeof (manuscriptChangeMeta.preview as { after?: unknown }).after === "string"
                  ? String((manuscriptChangeMeta.preview as { after: string }).after)
                  : undefined);
              const highlightDocumentId = selectedSavedOption?.documentId ?? (
                typeof manuscriptChangeMeta === "object"
                && manuscriptChangeMeta
                && !Array.isArray(manuscriptChangeMeta)
                && typeof (manuscriptChangeMeta as { documentId?: unknown }).documentId === "string"
                  ? String((manuscriptChangeMeta as { documentId: string }).documentId).trim()
                  : activeDocumentId);
              if (result.outcome === "applied" && highlightDocumentId) {
                dispatchAppliedManuscriptChangeHighlight(highlightDocumentId, previewAfter);
              }
              refetch?.();
              return;
            }
            if (result.outcome === "conflict") {
              setToast?.("I couldn't apply that change because the passage has changed.");
              return;
            }
            if (result.outcome === "failed") {
              setToast?.("I couldn't apply that change. Please try again.");
              return;
            }
            setToast?.("Could not apply the manuscript change. Please try again.");
          })
          .catch((error) => {
            Sentry.captureException(error, { tags: { feature: "manuscript_change_apply" } });
            setToast?.(safeManuscriptChangeApplyFailureMessage(error));
          })
          .finally(() => {
            typedApprovalInFlightRef.current = false;
          });
        return;
      }
      if (typedManuscriptChangeRef) {
        return;
      }
      runLocalRewriteApply();
    }, [
      activeDocumentId,
      comments,
      manuscriptChangeOwnership,
      message.metadata,
      messages,
      refetch,
      rewriteSuggestions,
      rewriteOptions,
      hasStructuredRewritePresentation,
      effectiveManuscriptChangeMeta,
      setToast,
      showTypedManuscriptChangeCarousel,
      showLiteralReplacementResult,
      tiptap,
      typedManuscriptChangeRef,
    ]);

    const handleSendRewriteSuggestionToNewDocument = useCallback(async (
      suggestionText: string,
      _index?: number,
      renderedSuggestionHtml?: string,
    ) => {
      const projectStore = useProjectStore.getState();
      const project = projectStore.project;
      if (!project?.id) {
        setToast?.("Unable to create document.");
        return;
      }

      const effectiveRenderedSuggestionHtml =
        renderedSuggestionHtml ?? renderMarkdownForTiptapInsert(suggestionText, { force: true });
      if (!effectiveRenderedSuggestionHtml) return;

      try {
        const content = buildSuggestionDocumentContent(effectiveRenderedSuggestionHtml);
        // Claim the global lock synchronously to avoid wait-then-set races
        // when multiple cards are clicked in rapid succession.
        if (projectStore.creatingDocument || project.creatingDocument) {
          return;
        }
        useProjectStore.getState().setCreatingDocument(true);
        if (project.creatingDocument) {
          return;
        }
        setToast?.("Creating document...", { durationMs: Infinity, dismissible: false });

        const result = await project.createDocument({
          content,
          waitForContentsInProject: false,
          ...(activeDocumentId ? { afterDocumentId: activeDocumentId } : {}),
        });
        const newDocumentId = result?.document?.id;
        if (!newDocumentId) {
          setToast?.("Unable to create document. Please try again.");
          return;
        }
        await useProjectStore.getState().setDocumentId(newDocumentId);
        setToast?.(null);
      } catch {
        setToast?.("Unable to create document. Please try again.");
      } finally {
        useProjectStore.getState().setCreatingDocument(false);
      }
    }, [activeDocumentId, setToast]);

    if (message.role === "user") {
      if ((message.metadata as any)?.planApproval) {
        return null;
      }

      const userVisibleContent = stripTrailingDocumentMentionsFromDisplay(
        entityGenerateUserMessageDisplayContent(messageContent, message.metadata as { ctaActionType?: string; generateInputs?: unknown }),
        message.metadata?.appendedDocumentMentions === true,
      );

      const isInlineEditing =
        !!inlineEditUserMessageId
        && message.id === inlineEditUserMessageId
        && !!onCancelInlineEditUserMessage
        && !!onSubmitInlineEditUserMessage;

      return <UserMessageBubble
        message={message}
        userVisibleContent={userVisibleContent}
        isInlineEditing={isInlineEditing}
        onCancelInlineEditUserMessage={onCancelInlineEditUserMessage}
        onSubmitInlineEditUserMessage={onSubmitInlineEditUserMessage}
        showEditMessageBelow={
          canShowInlineEditForUserMessageId?.(message.id) === true && !isInlineEditing
        }
        onEditMessageBelow={
          canShowInlineEditForUserMessageId?.(message.id)
            ? () => onEditTailUserMessage?.(message.id)
            : undefined
        }
        showResendLastMessage={showResendLastMessage}
        onResendLastMessage={onResendLastMessage}
        activeDocumentId={activeDocumentId}
        activeProjectId={activeProjectId}
        userBranchSiblingIndex={userBranchSiblingIndex}
        userBranchSiblingCount={userBranchSiblingCount}
        onUserBranchPrev={onUserBranchPrev}
        onUserBranchNext={onUserBranchNext}
      />;
    }

    return (
      <ChatMessageBubble role="assistant">
        <AssistantMessageBody dimmed={!!isRevertedStoryBibleChange}>
        {isRevertedStoryBibleChange ? (
          <div className="inline-flex items-center w-fit rounded-full border border-app-grey-3 bg-app-grey-4 px-2 py-0.5">
            <Text size="sm" className="text-app-grey-1">Reverted</Text>
          </div>
        ) : null}
        {storyBibleGeneratedEntity ? (
          <StoryBibleGeneratedEntityCard
            entity={storyBibleGeneratedEntity}
            activeDocumentId={activeDocumentId}
            activeProjectId={activeProjectId}
          />
        ) : null}

        {visibleThinkingSteps
          && visibleThinkingSteps.length > 0
          && !storyBibleGeneratedEntity && (
          <div className="flex flex-col gap-2 mb-3">
            {visibleThinkingSteps.map((step, index) => (
              <ThinkingStep
                key={`action-${index}`}
                data={step}
                model={assistantThinkingStepModel}
                writeSettings={assistantThinkingStepWriteSettings}
                writePovTenseChiclets={assistantThinkingStepWritePovTenseChiclets}
                contextElements={streaming ? streamingContextElements : (message.metadata?.contextElements as string[] | undefined)}
                mentionedEntities={streaming ? streamingMentionedEntities : (message.metadata?.mentionedEntities as Array<{ type: string; name: string }> | undefined)}
                highlightedPassage={message.metadata?.passage as string | undefined}
                insertedPassage={insertedPassage}
                referencedComments={Array.isArray(message.metadata?.referencedComments)
                  ? (message.metadata.referencedComments as Array<{ commentId: string; documentId?: string; previewText?: string }>).filter((comment) => comment?.commentId)
                  : message.metadata?.referencedCommentId
                    ? [{
                        commentId: message.metadata.referencedCommentId as string,
                        documentId: message.metadata?.referencedCommentDocumentId as string | undefined,
                      }]
                    : undefined}
                streaming={streaming}
                collapseForSuggestion={Boolean(rewriteSuggestions?.suggestions?.length)}
                icon={getProseActionIcon(message.metadata?.passage as string | undefined, step.tasks as ThinkingStepData["tasks"])}
                ctaAction={index === 0 ? ctaAction : undefined}
                onCtaGo={onCtaGo}
                onCtaCancel={onCtaCancel}
              />
            ))}
          </div>
        )}

        {previewGroups.length === 0 && showLiteralReplacementResult && typedManuscriptChangeRef ? (
          <LiteralReplacementResult
            wholeScope={showWholeScopeResult && !typedManuscriptChangeRef.preview?.literalReplacements?.length}
            change={typedManuscriptChangeRef}
            allowApply={!streaming && allowManuscriptChangeApply && Boolean(manuscriptChangeOwnership)
              && ["awaiting_consent", "ready_to_apply", "needs_review_again"].includes(typedManuscriptChangeRef.status)}
            allowUndo={!streaming && Boolean(typedManuscriptChangeRef.changeSetId && manuscriptChangeOwnership)
              && manuscriptChangeStatusShowsDurableUndo(typedManuscriptChangeRef.status)}
            onApply={() => handleApplyRewriteSuggestion("")}
            onUndo={handleDurableManuscriptUndo}
          />
        ) : null}

        {showAssistantProseSection ? (
          <div
            key={message.id}
            ref={assistantContentRef}
            className={cn("min-w-0", streaming ? "animate-in fade-in" : "", message.id === "error" ? "text-app-danger-color" : "")}
          >
            {message.id === "error" ? (
              // @ts-ignore
              <InlineWarning text={assistantMarkdownSource} />
            ) : previewGroups.length > 0 && orderedPreviews ? (
              <PreviewGroupStateProvider>
                <div className="flex flex-col gap-5">
                  {orderedPreviews.map((block, index) => block.type === 'text' ? (
                    <ReactMarkdown key={`text-${index}`} className="text-app-grey-0 text-base chat-markdown-content"
                      remarkPlugins={[remarkGfm, remarkDocumentMentions]} transformLinkUri={agenticChatTransformLinkUri} components={markdownComponents}>
                      {block.text}
                    </ReactMarkdown>
                  ) : (
                    <IndependentPreviewGroup key={`${block.group.key}:${block.optionIndex ?? 'compact'}`} group={block.group}
                      visibleOptionIndex={block.optionIndex} hideTitle chatId={chatId || message.chat_id || ""}
                      projectId={activeProjectId || chatProjectId || ""} streaming={streaming} refetch={refetch}
                      onSendSuggestionToNewDocument={handleSendRewriteSuggestionToNewDocument} />
                  ))}
                </div>
              </PreviewGroupStateProvider>
            ) : previewGroups.length > 0 ? (
              <div className="flex flex-col gap-5">
                {previewGroups.map((group) => (
                  <IndependentPreviewGroup key={group.key} group={group}
                    chatId={chatId || message.chat_id || ""} projectId={activeProjectId || chatProjectId || ""}
                    streaming={streaming} refetch={refetch} onSendSuggestionToNewDocument={handleSendRewriteSuggestionToNewDocument} />
                ))}
                {textBelowPreviewGroups ? (
                  <ReactMarkdown
                    className="text-app-grey-0 text-base chat-markdown-content"
                    remarkPlugins={[remarkGfm, remarkDocumentMentions]}
                    transformLinkUri={agenticChatTransformLinkUri}
                    components={markdownComponents}
                  >
                    {textBelowPreviewGroups}
                  </ReactMarkdown>
                ) : null}
              </div>
            ) : isProvisionalSuggestion && streamingPreviewGroups.some(group => group.id !== "legacy") ? (
              <div className="flex flex-col gap-5">
                {streamingPreviewGroups.map(group => (
                  <section key={group.id} aria-label={group.title || undefined} className="flex flex-col gap-3">
                    {group.title ? <h3 className="m-0 text-base font-semibold text-app-grey-0">{group.title}</h3> : null}
                    <RewriteSuggestionCarousel layout="stack" writeSuggestionCardHeader={writeSuggestionCardHeader}
                      suggestions={group.suggestions} explanations={group.explanations} status="streaming"
                      preserveExactText allowApply={false} onApplySuggestion={handleApplyRewriteSuggestion} />
                  </section>
                ))}
              </div>
            ) : rewriteSuggestions ? (
              <div className="flex flex-col gap-3">
                {rewriteSuggestions.intro ? (
                  <ReactMarkdown
                    className={cn("[&>*:first-child]:mt-0 [&>*:last-child]:mb-0", "text-app-grey-0 text-base", "chat-markdown-content")}
                    remarkPlugins={[remarkGfm, remarkDocumentMentions]}
                    transformLinkUri={agenticChatTransformLinkUri}
                    components={markdownComponents}
                  >
                    {rewriteSuggestions.intro}
                  </ReactMarkdown>
                ) : null}
                <RewriteSuggestionCarousel
                  layout="stack"
                  writeSuggestionCardHeader={writeSuggestionCardHeader}
                  suggestions={rewriteSuggestions.suggestions}
                  status={isProvisionalSuggestion ? "streaming" : typedManuscriptChangeRef?.status}
                  labels={typedManuscriptChangeSuggestions?.entries.map(entry => entry.label)}
                  explanations={isProvisionalSuggestion ? streamingExplanations : rewriteOptions?.options.map(option => option.explanation ?? "")}
                  preserveExactText={Boolean(rewriteOptions) || isProvisionalSuggestion}
                  onApplySuggestion={handleApplyRewriteSuggestion}
                  allowApply={!isProvisionalSuggestion && allowManuscriptChangeApply && (!hasStructuredRewritePresentation || (rewriteOptions !== null && rewriteOptionsCanApply(rewriteOptions, effectiveManuscriptChangeMeta)))}
                  appliedSuggestionIndex={effectiveAppliedSuggestionIndex}
                  onUndoAppliedSuggestion={handleCarouselManuscriptUndo}
                  onSendSuggestionToNewDocument={isProvisionalSuggestion ? undefined : handleSendRewriteSuggestionToNewDocument}
                />
                {manuscriptChangeOutcomeMessage ? (
                  <p className="m-0 text-base text-app-grey-0" role="status">
                    {manuscriptChangeOutcomeMessage}
                  </p>
                ) : null}
                {textBelowRewriteCarousel ? (
                  <ReactMarkdown
                    className={cn("[&>*:first-child]:mt-0 [&>*:last-child]:mb-0", "text-app-grey-0 text-base", "chat-markdown-content")}
                    remarkPlugins={[remarkGfm, remarkDocumentMentions]}
                    transformLinkUri={agenticChatTransformLinkUri}
                    components={markdownComponents}
                  >
                    {textBelowRewriteCarousel}
                  </ReactMarkdown>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {assistantMarkdownForDisplay ? (
                  <ReactMarkdown
                    className={cn("[&>*:first-child]:mt-0 [&>*:last-child]:mb-0", "text-app-grey-0 text-base", "chat-markdown-content")}
                    remarkPlugins={[remarkGfm, remarkDocumentMentions]}
                    transformLinkUri={agenticChatTransformLinkUri}
                    components={markdownComponents}
                  >
                    {assistantMarkdownForDisplay}
                  </ReactMarkdown>
                ) : null}
                {manuscriptChangeOutcomeMessage ? (
                  <p className="m-0 text-base text-app-grey-0" role="status">
                    {manuscriptChangeOutcomeMessage}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        ) : null}

        {questionAnswer && (
          <UserQuestionAnswerBubble
            text={questionAnswer.text}
            className="mt-2"
            stripAppendedDocumentMentions={questionAnswer.hasAppendedDocumentMentions}
          />
        )}

        {Array.isArray(message.metadata?.toolExecutions)
          ? message.metadata.toolExecutions.map((toolExecution: any, index: number) => {
              const ui = toolExecution?.result?.ui;
              // Legacy persisted `ui.type` values (pre–Chat-prefixed rename); keep rendering old threads.
              if (ui?.type === "ChatSupportRequestForm" || ui?.type === "SupportRequestForm") {
                const props = ui.props || {};
                return (
                  <ChatSupportRequestForm
                    key={`chat-support-request-${message.id}-${index}`}
                    chatId={message.chat_id}
                    messageId={message.id}
                    toolCallId={props.toolCallId}
                    projectId={props.projectId || message.metadata?.project_id}
                    invocationId={props.invocationId || message.metadata?.invocation_id}
                    category={props.category}
                    messageSummary={props.messageSummary ?? ""}
                    submissionStatus={props.submissionStatus ?? null}
                  />
                );
              }
              if (ui?.type === "ChatBugReportForm" || ui?.type === "BugReportForm") {
                const props = ui.props || {};
                return (
                  <ChatBugReportForm
                    key={`chat-bug-report-${message.id}-${index}`}
                    chatId={message.chat_id}
                    messageId={message.id}
                    toolCallId={props.toolCallId}
                    projectId={props.projectId || message.metadata?.project_id}
                    invocationId={props.invocationId || message.metadata?.invocation_id}
                    agentIssueSummary={props.agentIssueSummary ?? props.issueSummary ?? ""}
                    diagnosis={props.diagnosis ?? ""}
                    possibleSolutions={Array.isArray(props.possibleSolutions) ? props.possibleSolutions : []}
                    severity={props.severity ?? "medium"}
                    reportType={props.reportType ?? props.report_type ?? "bug"}
                    submissionStatus={props.submissionStatus ?? null}
                  />
                );
              }
              if (ui?.type === "RewriteSuggestionCarousel") {
                if (!showRewriteSuggestionCarousel || previewGroups.length > 0) return null;
                // Avoid rendering duplicate rewrite carousels when the assistant
                // message already resolved suggestions from metadata/text parsing.
                if (rewriteSuggestions) return null;
                if (
                  !toolResultAllowsCarouselRender({
                    toolName: toolExecution?.name,
                    result: toolExecution?.result,
                  })
                ) {
                  return null;
                }
                const props = ui.props || {};
                const toolSuggestions = Array.isArray(props.suggestions)
                  ? props.suggestions.filter(
                      (entry: unknown): entry is string => typeof entry === "string" && entry.trim().length > 0,
                    )
                  : [];
                const toolIntro = typeof props.intro === "string" ? props.intro : "";
                if (toolSuggestions.length < 1) return null; // SW-9663: render a single applyable suggestion too
                return (
                  <div key={`chat-rewrite-suggestions-${message.id}-${index}`} className="flex flex-col gap-3 mt-2">
                    {toolIntro ? (
                      <ReactMarkdown
                        className={cn("[&>*:first-child]:mt-0 [&>*:last-child]:mb-0", "text-app-grey-0 text-base", "chat-markdown-content")}
                        remarkPlugins={[remarkGfm]}
                        transformLinkUri={agenticChatTransformLinkUri}
                        components={markdownComponents}
                      >
                        {toolIntro}
                      </ReactMarkdown>
                    ) : null}
                    <RewriteSuggestionCarousel
                      layout="stack"
                      writeSuggestionCardHeader={writeSuggestionCardHeader}
                      suggestions={toolSuggestions}
                      onApplySuggestion={handleApplyRewriteSuggestion}
                      allowApply={!isProvisionalSuggestion && allowManuscriptChangeApply && (!hasStructuredRewritePresentation || (rewriteOptions !== null && rewriteOptionsCanApply(rewriteOptions, effectiveManuscriptChangeMeta)))}
                      appliedSuggestionIndex={effectiveAppliedSuggestionIndex}
                      onUndoAppliedSuggestion={handleCarouselManuscriptUndo}
                      onSendSuggestionToNewDocument={isProvisionalSuggestion ? undefined : handleSendRewriteSuggestionToNewDocument}
                    />
                  </div>
                );
              }
              return null;
            })
          : null}

        {(() => {
          const rawPlan = message.metadata?.plan as PlanData | undefined;
          const inlinePlan = rawPlan ? normalizeTerminalPlanStatus(rawPlan) : undefined;
          if (
            showInlineTerminalPlan
            && inlinePlan
            && (inlinePlan.status === "completed" || inlinePlan.status === "cancelled")
          ) {
            return (
              <div className="mt-2">
                <PlanCard
                  plan={inlinePlan}
                  mode="inline"
                  onDuplicatePlan={
                    mode === "write" && onDuplicatePlanRetry
                      ? () => onDuplicatePlanRetry(inlinePlan)
                      : undefined
                  }
                />
              </div>
            );
          }
          return null;
        })()}

        {(() => {
          const rawCommenting = message.metadata?.commenting as CommentingData | undefined;
          if (!showInlineTerminalCommenting || !rawCommenting || rawCommenting.status === "in_progress") return null;
          return (
            <div className="my-2">
              <CommentingCard
                commenting={rawCommenting}
                mode="inline"
              />
            </div>
          );
        })()}

        {!streaming && message.metadata?.commentCreationCorrectionApplied && onRetryCommentCreation && (
          <WithReset
            className={cn(
              "mt-2 w-full flex items-center justify-between px-3 py-3 rounded-xl",
              "border border-solid border-app-grey-3 hover:border-app-grey-2",
              "cursor-pointer transition-all active:scale-[0.99]",
            )}
            onClick={(e) => {
              e.preventDefault();
              onRetryCommentCreation();
            }}
            aria-label="Retry adding comments"
          >
            <Text size="base">Retry adding comments</Text>
            <InlineSVG
              className="w-5 h-5 flex-shrink-0 [&_path]:fill-app-primary-cta-high-contrast"
              src="/assets/send.svg"
            />
          </WithReset>
        )}

        {showStreamingSparkle
          && !thinkingSteps?.length && (
          <StreamingSparkle>
            <InlineSVG
              src="/assets/agentic-chat-sparkle.svg"
              className="w-4 h-4 [&_path]:stroke-app-primary-cta-high-contrast pulse-dot"
            />
          </StreamingSparkle>
        )}

        {!streaming &&
          !storyBibleGeneratedEntity &&
          message.id !== "error" &&
          message.id !== "tbd" &&
          !message.metadata?.questionAsked &&
          !message.metadata?.helpMenuGreeting &&
          message.metadata?.source !== "help_menu_agentic" && (
            <PersistentChatThumbs
              invocationId={message?.metadata?.invocation_id}
              content={message?.content}
              messageId={message.id}
              modelKey={message?.metadata?.agenticModelKey}
              mode={message?.metadata?.mode}
              alwaysVisible={isLastMessage}
              compact={showInlineTerminalPlan && !!message.metadata?.plan && !message.metadata?.planExecution}
              onCopy={handleAssistantCopy}
              initialRating={initialRating}
              streaming={streaming}
              feedbackByMessageId={feedbackByMessageId}
              precedingUserMessageId={findPrecedingUserMessageId(messages, message.id)}
              onFeedbackTryAgain={onFeedbackTryAgain}
              onFeedbackSwitchToChatMode={onFeedbackSwitchToChatMode}
              onFeedbackStartFreshChat={onFeedbackStartFreshChat}
              hideRatingAndCopy={hasRewriteSuggestionCarousel}
              storyBibleRevertButtons={
                !isRevertedStoryBibleChange
                && revertOption
                && hasRestorableAgenticSnapshotBundle(revertOption.snapshotBundle) ? (
                  <RevertAgentChangesButton
                    revertOption={revertOption}
                    assistantMessageId={revertTargetAssistantMessageId ?? message.id}
                    snapshotBundle={revertOption.snapshotBundle}
                    label="Undo last change from chat"
                    streaming={streaming}
                    isLastRevertibleAssistantMessage={isLastRevertibleAssistantMessage}
                    buttonClassName="pl-2"
                    onRevertSuccess={() => onRevertStoryBibleSuccess?.(revertTargetAssistantMessageId ?? message.id)}
                    revertInFlight={storyBibleRevertInFlight}
                    onRevertStart={() => setStoryBibleRevertInFlight(true)}
                    onRevertEnd={() => setStoryBibleRevertInFlight(false)}
                  />
                ) : null
              }
              showManuscriptUndo={
                !streaming
                && !showLiteralReplacementResult
                && isLastAssistantMessage
                && effectiveAppliedSuggestionIndex == null
                && (
                  showDocumentTextEditAccept
                  || manuscriptChangeRefShowsDurableUndo(typedManuscriptChangeRef)
                )
              }
              onManuscriptUndo={typedManuscriptChangeRef?.changeSetId
                ? handleDurableManuscriptUndo
                : undefined}
              onManuscriptUndoSuccess={() => setManuscriptEditUndone(true)}
            />
          )}

        {searchedDocs?.length || lookingAtData?.length || cutoffData || contextWarning ? (
          <ul className="flex-row flex-wrap items-center justify-start flex gap-1">
            {searchedDocs?.slice(0, 5).map((doc) => (
              <li
                key={doc}
                className={cn("rounded-lg bg-app-primary-cta-translucent w-auto pl-1 pr-1.5 py-1 text-sm flex items-center gap-0.5", streaming ? "animate-in zoom-in" : "")}
              >
                <InlineSVG
                  src="/assets/document.svg"
                  className="w-5 h-5 [&_path]:fill-app-grey-0"
                  aria-hidden="true"
                />
                <Text className="text-sm">{doc}</Text>
              </li>
            ))}
            {lookingAtData?.map((type) => (
              <li
                key={type}
                className={cn("rounded-lg bg-app-primary-cta-translucent w-auto pl-1 pr-1.5 py-1 text-sm flex items-center gap-0.5", streaming ? "animate-in zoom-in" : "")}
              >
                <InlineSVG
                  src={iconTypeMap[type as keyof typeof iconTypeMap]}
                  className="w-5 h-5 [&_path]:fill-app-grey-0"
                  aria-hidden="true"
                />
                <Text className="text-sm">{type.charAt(0).toUpperCase() + type.slice(1)}</Text>
              </li>
            ))}

            {cutoffData ? (
              <li className={cn("rounded-lg bg-app-primary-cta-translucent w-auto pl-1 pr-1.5 py-1 text-sm flex items-center gap-0.5", streaming ? "animate-in zoom-in" : "")}>
                <InlineSVG
                  src="/assets/warning.svg"
                  className="w-5 h-5 [&_path]:fill-app-grey-0 pb-1"
                  aria-hidden="true"
                />
                <Text className="text-sm">
                  Skipped {cutoffData.originalLength - cutoffData.newLength} message{cutoffData.originalLength - cutoffData.newLength > 1 ? "s" : ""}
                </Text>
              </li>
            ) : null}

            {contextWarning ? (
              <li className={cn("rounded-lg bg-yellow-100 w-full p-1.5 text-sm flex items-center gap-2", streaming ? "animate-in zoom-in" : "")}>
                <InlineSVG
                  src="/assets/warning.svg"
                  className="w-10 h-10 [&_path]:fill-yellow-800 pb-1"
                  aria-hidden="true"
                />
                <Text className="text-sm text-yellow-800">{contextWarning.message}</Text>
              </li>
            ) : null}
          </ul>
        ) : null}
        </AssistantMessageBody>
      </ChatMessageBubble>
    );
  },
);

const UserMessageBubble: React.FC<{
  message: PersistentChatMessageData;
  userVisibleContent: string;
  isInlineEditing: boolean;
  onCancelInlineEditUserMessage?: () => void;
  onSubmitInlineEditUserMessage?: (userMessageId: string, text: string) => void;
  showEditMessageBelow?: boolean;
  onEditMessageBelow?: () => void;
  showResendLastMessage?: boolean;
  onResendLastMessage?: () => void;
  activeDocumentId?: string | null;
  activeProjectId?: string | null;
  userBranchSiblingIndex?: number;
  userBranchSiblingCount?: number;
  onUserBranchPrev?: () => void;
  onUserBranchNext?: () => void;
}> = memo(({
  message,
  userVisibleContent,
  isInlineEditing,
  onCancelInlineEditUserMessage,
  onSubmitInlineEditUserMessage,
  showEditMessageBelow,
  onEditMessageBelow,
  showResendLastMessage,
  onResendLastMessage,
  activeDocumentId,
  activeProjectId,
  userBranchSiblingIndex,
  userBranchSiblingCount,
  onUserBranchPrev,
  onUserBranchNext,
}) => {
  const [copied, setCopied] = useState(false);
  const [inlineDraft, setInlineDraft] = useState("");
  const editRef = useRef<HTMLTextAreaElement | null>(null);
  const isMac = useAppStore((state) => state.isMac);

  useEffect(() => {
    if (isInlineEditing) {
      setInlineDraft(userVisibleContent ?? "");
    }
  }, [isInlineEditing, message.id, userVisibleContent]);

  useEffect(() => {
    if (!isInlineEditing) return;
    const id = requestAnimationFrame(() => {
      const el = editRef.current;
      if (!el) return;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    });
    return () => cancelAnimationFrame(id);
  }, [isInlineEditing]);

  const handleInlineBranchSend = useCallback(() => {
    if (!inlineDraft.trim()) return;
    onSubmitInlineEditUserMessage?.(message.id, inlineDraft);
  }, [inlineDraft, message.id, onSubmitInlineEditUserMessage]);

  const handleCopy = useCallback(() => {
    if (copied) return;
    setCopied(true);
    const plainText = extractPlainTextFromMentions(userVisibleContent);
    AppEventPublisher.agenticChat.copyMessage("user", plainText.length);
    navigator.clipboard.writeText(plainText);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }, [copied, userVisibleContent]);

  const hasBranchPager =
    userBranchSiblingCount != null
    && userBranchSiblingCount > 1
    && userBranchSiblingIndex != null
    && !isInlineEditing;

  return (
    <div
      className="flex flex-col w-full group"
      data-user-message-id={message.id}
      data-inline-edit-user-message={isInlineEditing ? message.id : undefined}
    >
      <SharedUserMessageBubble
        isEditing={isInlineEditing}
        renderEditor={() => (
          <Textarea
            ref={editRef}
            value={inlineDraft}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setInlineDraft(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === "Escape") {
                e.preventDefault();
                onCancelInlineEditUserMessage?.();
                return;
              }
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleInlineBranchSend();
              }
            }}
            mode="default"
            className="!rounded-[10px] w-full min-w-0 !bg-white dark:!bg-app-grey-5"
            minRows={3}
            short
            enableMentions={false}
          />
        )}
        onCancelEdit={onCancelInlineEditUserMessage}
        onSubmitEdit={handleInlineBranchSend}
        submitDisabled={!inlineDraft.trim()}
        sendShortcut={(
          <span className="flex items-center gap-1" aria-hidden="true">
            <KeybardShortcutIcon
              name={isMac ? "command" : "control"}
              style={{ width: 21, height: 19 }}
            />
            <KeybardShortcutIcon
              name="enter"
              style={{ width: 21, height: 19 }}
            />
          </span>
        )}
        onCopy={handleCopy}
        copied={copied}
        showCopy
        showEdit={!!showEditMessageBelow && !!onEditMessageBelow}
        onEdit={onEditMessageBelow}
        footerAlwaysVisible={!!showEditMessageBelow}
        branch={hasBranchPager
          ? {
              index: userBranchSiblingIndex!,
              count: userBranchSiblingCount!,
              onPrev: onUserBranchPrev,
              onNext: onUserBranchNext,
            }
          : undefined}
        showResend={!!showResendLastMessage && !!onResendLastMessage}
        onResend={onResendLastMessage}
        icons={defaultChatIcons}
      >
        <div className="min-w-0">
          <Text
            as="div"
            className="min-w-0 text-left break-words"
          >
            <MentionMarkdown
              text={userVisibleContent}
              activeDocumentId={activeDocumentId}
              activeProjectId={activeProjectId}
            />
          </Text>
        </div>
      </SharedUserMessageBubble>
    </div>
  );
});

UserMessageBubble.displayName = "UserMessageBubble";

const submitFeedback = async (
  params: {
    chatId: string;
    messageId: string;
    projectId: string;
    rating: number;
    feedbackText?: string;
    modelKey?: string;
    mode?: string;
  },
  queryClient?: QueryClient,
) => {
  await Auth.fetchWithIdToken("/api/ai/agenticChat/feedback", params);
  if (queryClient) {
    patchChatFeedbackRatingInCache(queryClient, {
      projectId: params.projectId,
      chatId: params.chatId,
      messageId: params.messageId,
      rating: params.rating,
    });
  }
};

const PersistentChatThumbs: React.FC<{
  invocationId: string;
  content: string;
  messageId: string;
  modelKey?: string;
  mode?: string;
  alwaysVisible?: boolean;
  compact?: boolean;
  onCopy: () => Promise<void> | void;
  initialRating?: number | null;
  streaming?: boolean;
  feedbackByMessageId?: Record<string, number>;
  precedingUserMessageId?: string | null;
  onFeedbackTryAgain?: (params: {
    assistantMessageId: string;
    precedingUserMessageId: string | null;
    mode: "write" | "chat";
    feedbackText?: string;
  }) => void;
  onFeedbackSwitchToChatMode?: () => void;
  onFeedbackStartFreshChat?: () => void;
  hideRatingAndCopy?: boolean;
  storyBibleRevertButtons?: ReactNode;
  showManuscriptUndo?: boolean;
  onManuscriptUndoSuccess?: () => void;
  onManuscriptUndo?: () => Promise<boolean>;
}> = ({
  invocationId,
  content,
  messageId,
  modelKey,
  mode,
  alwaysVisible,
  compact = false,
  onCopy,
  initialRating,
  streaming = false,
  feedbackByMessageId,
  precedingUserMessageId = null,
  onFeedbackTryAgain,
  onFeedbackSwitchToChatMode,
  onFeedbackStartFreshChat,
  hideRatingAndCopy = false,
  storyBibleRevertButtons,
  showManuscriptUndo = false,
  onManuscriptUndoSuccess,
  onManuscriptUndo,
}) => {
  const { chatId, projectId } = usePersistentChat();
  const queryClient = useQueryClient();
  const agentIsRunning = usePersistentChatStore(
    (s) => s.streaming || s.pendingAgentResponse || isCommentingKeepingChatTurnOpen(s.activeCommenting),
  );
  const isMac = useAppStore((state) => state.isMac);
  const setToast = useToast()?.setToast;
  const [liked, setLiked] = useState<number | null>(initialRating ?? null);
  const [copied, setCopied] = useState(false);
  const [manuscriptUndoState, setManuscriptUndoState] = useState<"idle" | "undoing" | "done">("idle");
  const manuscriptUndoCleanupRef = useRef<(() => void) | null>(null);
  const [showFeedbackPopover, setShowFeedbackPopover] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const feedbackPersistedRef = useRef<number | null>(null);

  const resolvedMode = (mode === "chat" ? "chat" : "write") as "write" | "chat";
  const showRepeatedDislikeRecovery = liked === -1
    && shouldShowRepeatedDislikeRecovery(feedbackByMessageId, liked === -1 && initialRating !== -1);

  const persistFeedback = useCallback(async (rating: number, text?: string) => {
    await submitFeedback({
      chatId,
      messageId,
      projectId,
      rating,
      feedbackText: text?.trim() || undefined,
      modelKey,
      mode,
    }, queryClient);
    feedbackPersistedRef.current = rating;
  }, [chatId, messageId, projectId, modelKey, mode, queryClient]);

  const handleThumbClick = useCallback((newRating: -1 | 1) => {
    const isToggleOff = liked === newRating;

    if (isToggleOff) {
      setLiked(null);
      setShowFeedbackPopover(false);
      feedbackPersistedRef.current = null;
      const event = newRating === -1 ? AgenticChatFeedback.Undislike : AgenticChatFeedback.Unlike;
      AppEventPublisher.agenticChat.feedback(event, invocationId);
      sendInvocationEvent(invocationId, newRating === -1 ? "undislike" : "unlike");
      submitFeedback({ chatId, messageId, projectId, rating: 0, modelKey, mode }, queryClient).catch(() => {});
    } else {
      setLiked(newRating);
      feedbackPersistedRef.current = null;
      const event = newRating === -1 ? AgenticChatFeedback.Dislike : AgenticChatFeedback.Like;
      AppEventPublisher.agenticChat.feedback(event, invocationId);
      sendInvocationEvent(invocationId, newRating === -1 ? "dislike" : "like");
      setFeedbackText("");
      setFeedbackSubmitted(false);
      setShowFeedbackPopover(true);
    }
  }, [liked, invocationId, chatId, messageId, projectId, modelKey, mode, queryClient]);

  const handleFeedbackSubmit = useCallback(async () => {
    if (liked === null || feedbackSubmitting) return;
    setFeedbackSubmitting(true);
    await persistFeedback(liked, feedbackText).catch(() => {});
    setFeedbackSubmitting(false);
    setFeedbackSubmitted(true);
    setTimeout(() => {
      setShowFeedbackPopover(false);
    }, 1200);
  }, [liked, feedbackText, feedbackSubmitting, persistFeedback]);

  const handleFeedbackSkip = useCallback(() => {
    if (liked === null) return;
    setShowFeedbackPopover(false);
    if (feedbackPersistedRef.current === liked) return;
    persistFeedback(liked).catch(() => {});
  }, [liked, persistFeedback]);

  const handleFeedbackPopoverOpenChange = useCallback((open: boolean) => {
    if (open) {
      setShowFeedbackPopover(true);
      return;
    }

    if (feedbackSubmitted || feedbackSubmitting || feedbackPersistedRef.current === liked) {
      setShowFeedbackPopover(false);
      return;
    }

    handleFeedbackSkip();
  }, [feedbackSubmitted, feedbackSubmitting, liked, handleFeedbackSkip]);

  const handleTryAgain = useCallback(async () => {
    if (liked !== -1 || !onFeedbackTryAgain || agentIsRunning) return;
    setFeedbackSubmitting(true);
    await persistFeedback(-1, feedbackText).catch(() => {});
    setFeedbackSubmitting(false);
    setShowFeedbackPopover(false);
    onFeedbackTryAgain({
      assistantMessageId: messageId,
      precedingUserMessageId: precedingUserMessageId ?? null,
      mode: resolvedMode,
      feedbackText: feedbackText.trim() || undefined,
    });
  }, [
    liked,
    onFeedbackTryAgain,
    agentIsRunning,
    persistFeedback,
    feedbackText,
    messageId,
    precedingUserMessageId,
    resolvedMode,
  ]);

  const persistDislikeBeforeRecoveryShortcut = useCallback(async () => {
    if (liked !== -1 || feedbackSubmitting) return false;
    setFeedbackSubmitting(true);
    if (feedbackPersistedRef.current !== liked) {
      await persistFeedback(-1, feedbackText).catch(() => {});
    }
    setFeedbackSubmitting(false);
    setShowFeedbackPopover(false);
    return true;
  }, [liked, feedbackSubmitting, persistFeedback, feedbackText]);

  const handleSwitchToChatMode = useCallback(async () => {
    if (!onFeedbackSwitchToChatMode) return;
    const ready = await persistDislikeBeforeRecoveryShortcut();
    if (!ready) return;
    onFeedbackSwitchToChatMode();
  }, [onFeedbackSwitchToChatMode, persistDislikeBeforeRecoveryShortcut]);

  const handleStartFreshChat = useCallback(async () => {
    if (!onFeedbackStartFreshChat) return;
    const ready = await persistDislikeBeforeRecoveryShortcut();
    if (!ready) return;
    onFeedbackStartFreshChat();
  }, [onFeedbackStartFreshChat, persistDislikeBeforeRecoveryShortcut]);

  useEffect(() => () => {
    manuscriptUndoCleanupRef.current?.();
    manuscriptUndoCleanupRef.current = null;
  }, []);

  const manuscriptUndoFailureMessage = useMemo(() => {
    const historyShortcut = isMac ? "⇧⌘H" : "Ctrl+Shift+H";
    return `Nothing to undo for this edit. Open Document History (${historyShortcut}) to restore a previous version.`;
  }, [isMac]);

  const handleManuscriptUndo = useCallback(() => {
    if (manuscriptUndoState !== "idle") return;
    setManuscriptUndoState("undoing");

    if (onManuscriptUndo) {
      void onManuscriptUndo()
        .then((ok) => {
          if (ok) {
            window.dispatchEvent(new CustomEvent("sudo-agent-highlight-edit", { detail: { clear: true } }));
            onManuscriptUndoSuccess?.();
            setManuscriptUndoState("done");
          } else {
            setManuscriptUndoState("idle");
          }
        })
        .catch(() => setManuscriptUndoState("idle"));
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      window.removeEventListener("sudo-agent-manuscript-undo-result", onResult);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      manuscriptUndoCleanupRef.current = null;
    };

    const onResult = (event: Event) => {
      cleanup();
      const ok = Boolean((event as CustomEvent<{ ok?: boolean }>).detail?.ok);
      if (ok) {
        window.dispatchEvent(new CustomEvent("sudo-agent-highlight-edit", { detail: { clear: true } }));
        onManuscriptUndoSuccess?.();
        setToast?.("Undid the last manuscript change.");
        setManuscriptUndoState("done");
      } else {
        setToast?.(manuscriptUndoFailureMessage);
        setManuscriptUndoState("idle");
      }
    };

    timeoutId = window.setTimeout(() => {
      cleanup();
      setToast?.(manuscriptUndoFailureMessage);
      setManuscriptUndoState("idle");
    }, 3500);

    manuscriptUndoCleanupRef.current = cleanup;
    window.addEventListener("sudo-agent-manuscript-undo-result", onResult);
    window.dispatchEvent(new CustomEvent("sudo-agent-manuscript-undo", { bubbles: false }));
  }, [manuscriptUndoFailureMessage, manuscriptUndoState, onManuscriptUndo, onManuscriptUndoSuccess, setToast]);

  const showManuscriptUndoControl = showManuscriptUndo && manuscriptUndoState !== "done";
  if (hideRatingAndCopy && !storyBibleRevertButtons && !showManuscriptUndoControl) {
    return null;
  }

  const iconClassName = cn(
    "cursor-pointer hover:scale-105 transition-all duration-200 ease-in-out active:scale-95",
  );
  const rowClassName = cn(
    "animate-in fade-in",
    compact && !alwaysVisible && !showManuscriptUndoControl && "!min-h-0",
    alwaysVisible || showManuscriptUndoControl
      ? "opacity-100 h-6"
      : "opacity-0 h-px overflow-hidden group-hover:opacity-100 group-hover:h-6 origin-top",
  );

  const feedbackPopoverContent = liked === 1 || liked === -1 ? (
    <AgenticChatFeedbackPopover
      liked={liked}
      feedbackText={feedbackText}
      onFeedbackTextChange={setFeedbackText}
      feedbackSubmitting={feedbackSubmitting}
      feedbackSubmitted={feedbackSubmitted}
      onSubmit={handleFeedbackSubmit}
      onSkip={handleFeedbackSkip}
      onTryAgain={liked === -1 ? handleTryAgain : undefined}
      onSwitchToChatMode={liked === -1 ? handleSwitchToChatMode : undefined}
      onStartFreshChat={liked === -1 ? handleStartFreshChat : undefined}
      showRepeatedDislikeRecovery={showRepeatedDislikeRecovery}
      showSwitchToChatMode={resolvedMode === "write"}
      tryAgainDisabled={agentIsRunning}
    />
  ) : null;

  const handleCopyClick = async () => {
    if (copied) return;
    setCopied(true);
    try {
      AppEventPublisher.agenticChat.copyMessage("assistant", content?.length || 0);
      await onCopy();
    } catch (_error) {
      // Ignore clipboard failures; UI still resets copied state below.
    } finally {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };

  return (
    <ChatMessageActionRow
      showRatingAndCopy={!hideRatingAndCopy}
      rating={liked === -1 ? -1 : liked === 1 ? 1 : null}
      onThumbDown={() => handleThumbClick(-1)}
      onThumbUp={() => handleThumbClick(1)}
      copied={copied}
      onCopy={handleCopyClick}
      alwaysVisible={alwaysVisible}
      rowClassName={rowClassName}
      iconClassName={iconClassName}
      extraActions={storyBibleRevertButtons}
      showUndo={showManuscriptUndoControl}
      undoBusy={manuscriptUndoState === "undoing"}
      onUndo={handleManuscriptUndo}
      icons={defaultChatIcons}
      wrapRatingControls={(controls) => (
        <GenericPopover
          open={showFeedbackPopover}
          onOpenChange={handleFeedbackPopoverOpenChange}
          side="top"
          align="start"
          sideOffset={8}
          modal={true}
          maxHeight="auto"
          width="320px"
          trigger={<WithReset className="flex-row gap-1 items-center flex">{controls}</WithReset>}
        >
          {feedbackPopoverContent}
        </GenericPopover>
      )}
    />
  );
};
